<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <v-icon class="mr-2" color="primary">mdi-file-document-multiple</v-icon>
              <span>{{ $t('admin.testManagement') || 'Управление тестами' }}</span>
            </div>
            <div class="d-flex gap-2 flex-wrap">
              <!-- Tools dropdown: audit/quality + topic editor + PDF generator -->
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn color="info" variant="outlined" v-bind="props">
                    <v-icon class="mr-2">mdi-tools</v-icon>
                    {{ $t('admin.tools') || 'Құралдар' }}
                    <v-icon class="ml-1">mdi-chevron-down</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact" min-width="240">
                  <v-list-item @click="openDuplicatesDialog" :disabled="duplicatesLoading">
                    <template v-slot:prepend>
                      <v-icon color="warning">mdi-content-duplicate</v-icon>
                    </template>
                    <v-list-item-title>{{ $t('admin.duplicates') || 'Дубликаттар' }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="openLanguageMismatchDialog" :disabled="languageMismatchLoading">
                    <template v-slot:prepend>
                      <v-icon color="info">mdi-translate-off</v-icon>
                    </template>
                    <v-list-item-title>{{ $t('admin.languageErrors') || 'Тіл қателері' }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="openTopicEditorDialog">
                    <template v-slot:prepend>
                      <v-icon color="purple">mdi-tag-multiple</v-icon>
                    </template>
                    <v-list-item-title>{{ $t('admin.topicEditor') || 'Тақырыптар редакторы' }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="openUntakenDialog" :disabled="untakenLoading">
                    <template v-slot:prepend>
                      <v-icon color="orange">mdi-test-tube-off</v-icon>
                    </template>
                    <v-list-item-title>{{ $t('admin.untakenTests') || 'Тапсырылмаған тесттер' }}</v-list-item-title>
                  </v-list-item>
                  <v-divider class="my-1" />
                  <v-list-item @click="openPdfGeneratorDialog">
                    <template v-slot:prepend>
                      <v-icon color="success">mdi-file-pdf-box</v-icon>
                    </template>
                    <v-list-item-title>{{ $t('admin.pdfGenerator') || 'PDF генератор' }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>

              <!-- Import / Templates dropdown -->
              <v-menu :close-on-content-click="true">
                <template v-slot:activator="{ props }">
                  <v-btn color="success" variant="outlined" v-bind="props">
                    <v-icon class="mr-2">mdi-file-import</v-icon>
                    {{ $t('admin.importExport') || 'Импорт / Шаблон' }}
                    <v-icon class="ml-1">mdi-chevron-down</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact" min-width="260">
                  <v-list-item @click="openImportDialog">
                    <template v-slot:prepend>
                      <v-icon color="primary">mdi-file-upload</v-icon>
                    </template>
                    <v-list-item-title>{{ $t('admin.importTests') || 'Импорт' }}</v-list-item-title>
                    <v-list-item-subtitle>txt, xlsx, json</v-list-item-subtitle>
                  </v-list-item>
                  <v-divider class="my-1" />
                  <v-list-subheader>{{ $t('admin.downloadTemplate') || 'Шаблон жүктеу' }}</v-list-subheader>
                  <v-list-item @click="downloadTemplate('json')">
                    <template v-slot:prepend>
                      <v-icon color="orange">mdi-code-json</v-icon>
                    </template>
                    <v-list-item-title>JSON</v-list-item-title>
                    <v-list-item-subtitle>test_template.json</v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item @click="downloadTemplate('txt')">
                    <template v-slot:prepend>
                      <v-icon color="blue">mdi-text</v-icon>
                    </template>
                    <v-list-item-title>TXT</v-list-item-title>
                    <v-list-item-subtitle>test_template.txt</v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item @click="downloadTemplate('xlsx')">
                    <template v-slot:prepend>
                      <v-icon color="green">mdi-file-excel</v-icon>
                    </template>
                    <v-list-item-title>Excel</v-list-item-title>
                    <v-list-item-subtitle>test_template.xlsx</v-list-item-subtitle>
                  </v-list-item>
                </v-list>
              </v-menu>

              <v-btn
                color="primary"
                @click="openCreateDialog"
                :loading="loading"
              >
                <v-icon class="mr-2">mdi-plus</v-icon>
                {{ $t('admin.createTest') || 'Тест жасау' }}
              </v-btn>
              <v-btn
                color="error"
                variant="outlined"
                @click="openDeleteAllDialog"
                :disabled="!filteredTests || filteredTests.length === 0"
              >
                <v-icon class="mr-2">mdi-delete-sweep</v-icon>
                {{ $t('admin.deleteAllTests') || 'Барлығын өшіру' }}
              </v-btn>
            </div>
          </v-card-title>
          

          <v-card-text>
            <!-- Search and filters -->
            <v-row class="mb-4">
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="searchQuery"
                  :label="$t('common.search') || 'Поиск'"
                  prepend-inner-icon="mdi-magnify"
                  variant="outlined"
                  density="compact"
                  clearable
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-select
                  v-model="selectedTopic"
                  :items="uniqueTopics"
                  :label="$t('test.topic') || 'Тақырып'"
                  variant="outlined"
                  density="compact"
                  clearable
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-select
                  v-model="selectedErrorFilter"
                  :items="errorFilterOptions"
                  :label="$t('admin.errorFilter') || 'Қате фильтрі'"
                  variant="outlined"
                  density="compact"
                  clearable
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-btn
                  color="secondary"
                  variant="outlined"
                  @click="clearFilters"
                  block
                >
                  {{ $t('common.clearFilters') || 'Очистить' }}
                </v-btn>
              </v-col>
            </v-row>

            <!-- Auto-clean button (only visible when error filter is active) -->
            <v-row v-if="selectedErrorFilter === 'hasErrors'" class="mb-4">
              <v-col cols="12">
                <v-alert
                  v-if="testsWithErrors.length > 0"
                  type="info"
                  variant="tonal"
                  density="compact"
                  class="mb-3"
                >
                  <div class="d-flex align-center">
                    <v-icon class="mr-2">mdi-information</v-icon>
                    <div class="flex-grow-1">
                      <strong>{{ testsWithErrors.length }}</strong>
                      {{ $t('admin.errorAlertFound') || 'тестте қателер табылды. Бос өрістер мен дұрыс емес деректер бар.' }}
                    </div>
                    <v-btn
                      color="warning"
                      variant="elevated"
                      size="small"
                      @click="openAutoCleanDialog"
                      :loading="autoCleanLoading"
                    >
                      <v-icon class="mr-2">mdi-auto-fix</v-icon>
                      {{ $t('admin.autoClean') || 'Автоматты тазалау' }}
                    </v-btn>
                  </div>
                </v-alert>
                <v-alert
                  v-else
                  type="success"
                  variant="tonal"
                  density="compact"
                  class="mb-3"
                >
                  <div class="d-flex align-center">
                    <v-icon class="mr-2">mdi-check-circle</v-icon>
                    <div class="flex-grow-1">
                      <strong>{{ $t('admin.errorAlertNoneTitle') || 'Тамаша!' }}</strong>
                      {{ $t('admin.errorAlertNone') || 'Барлық тесттер дұрыс форматта. Қатесі бар тесттер табылмады.' }}
                    </div>
                  </div>
                </v-alert>
              </v-col>
            </v-row>

            <!-- Tests table -->
            <v-data-table
              :headers="headers"
              :items="filteredTests"
              :loading="loading"
              :items-per-page="50"
              :items-per-page-options="[10, 25, 50, 100, -1]"
              :items-per-page-text="$t('common.itemsPerPage') || 'Элементов на странице:'"
              class="elevation-1"
            >
              <template v-slot:item.number="{ index }">
                <span class="text-caption text-medium-emphasis">{{ index + 1 }}</span>
              </template>

              <template v-slot:item.question="{ item }">
                <div class="text-wrap py-2">
                  <div 
                    :class="{ 'text-error': isEmptyOrWhitespace(item.question) }"
                    class="text-body-2 mb-1"
                  >
                    {{ getQuestionByLocale(item) || ('(' + ($t('admin.emptyQuestion') || 'Бос сұрақ') + ')') }}
                  </div>
                </div>
              </template>

              <template v-slot:item.topic="{ item }">
                <div class="text-wrap py-2">
                  <div class="text-body-2">
                    {{ item.topic || '—' }}
                  </div>
                </div>
              </template>

              <template v-slot:item.actions="{ item }">
                <!-- Warning icon for any backend-detected issues -->
                <v-tooltip
                  v-if="describeTestErrors(item).length > 0"
                  :text="describeTestErrors(item).join(' · ')"
                >
                  <template v-slot:activator="{ props }">
                    <v-icon
                      v-bind="props"
                      color="error"
                      size="small"
                      class="mr-1"
                    >
                      mdi-alert-circle
                    </v-icon>
                  </template>
                </v-tooltip>

                <v-tooltip :text="$t('common.edit') || 'Өңдеу'">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      v-bind="props"
                      icon="mdi-pencil"
                      size="small"
                      variant="text"
                      @click="editTest(item)"
                    />
                  </template>
                </v-tooltip>

                <v-tooltip :text="$t('common.delete') || 'Өшіру'">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      v-bind="props"
                      icon="mdi-delete"
                      size="small"
                      variant="text"
                      color="error"
                      @click="deleteTest(item)"
                    />
                  </template>
                </v-tooltip>

                <v-tooltip :text="$t('admin.viewStats') || 'Статистиканы көру'">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      v-bind="props"
                      icon="mdi-chart-line"
                      size="small"
                      variant="text"
                      color="info"
                      @click="viewStats(item)"
                    />
                  </template>
                </v-tooltip>
              </template>

              <template v-slot:no-data>
                <div class="text-center pa-4">
                  <v-icon size="64" color="grey">mdi-file-document-outline</v-icon>
                  <div class="text-h6 mt-2">{{ $t('admin.noTests') || 'Нет тестов' }}</div>
                  <div class="text-body-2 text-medium-emphasis">
                    {{ $t('admin.createFirstTest') || 'Создайте первый тест для начала работы' }}
                  </div>
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Create/Edit Test Dialog -->
    <v-dialog v-model="testDialog" max-width="1200px" persistent>
      <v-card class="compact-form-card">
        <v-card-title class="py-2 px-4 d-flex align-center">
          <span class="text-h6">
            {{ editingTest ? ($t('admin.editTest') || 'Редактировать тест') : ($t('admin.createTest') || 'Создать тест') }}
          </span>
          <v-spacer />
          <v-btn
            icon
            variant="text"
            size="small"
            @click="confirmCloseTestDialog"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
          <v-form ref="testForm" v-model="formValid">
            <!-- Single Language Selector at the top -->
            <v-row dense>
              <v-col cols="12">
                <v-card variant="outlined" class="mb-2">
                  <v-card-title class="text-subtitle-1 py-1 px-3">
                    <v-icon class="mr-2" size="small">mdi-translate</v-icon>
                    {{ $t('admin.languageSelection') || 'Тіл таңдау' }}
                  </v-card-title>
                  <v-card-text class="pt-0 pb-1 px-3">
                    <v-tabs v-model="currentLanguage" bg-color="primary" density="compact" height="32" class="mb-1">
                      <v-tab value="ru" class="text-caption px-2">
                        <v-icon start size="x-small">mdi-translate</v-icon>
                        Русский
                      </v-tab>
                      <v-tab value="kz" class="text-caption px-2">
                        <v-icon start size="x-small">mdi-translate</v-icon>
                        Қазақша
                      </v-tab>
                      <v-tab value="en" class="text-caption px-2">
                        <v-icon start size="x-small">mdi-translate</v-icon>
                        English
                      </v-tab>
                    </v-tabs>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- Question Section -->
            <v-row dense>
              <v-col cols="12">
                <v-card variant="outlined" class="mb-2">
                  <v-card-title class="text-subtitle-1 py-1 px-3">
                    {{ $t('test.question') || 'Вопрос' }}
                  </v-card-title>
                  
                  <v-card-text class="pt-0 pb-2 px-3">
                    <v-window v-model="currentLanguage">
                      <v-window-item value="ru">
                        <v-textarea
                          v-model="testData.questionRu"
                          label="Вопрос на русском"
                          :rules="[v => !!v || 'Вопрос на русском обязателен']"
                          :error="!testData.questionRu && showValidationErrors"
                          :error-messages="!testData.questionRu && showValidationErrors ? 'Орыс тіліндегі сұрақ міндетті' : []"
                          required
                          variant="outlined"
                          rows="2"
                          auto-grow
                          density="compact"
                          hide-details="auto"
                          class="compact-input"
                          append-inner-icon="mdi-arrow-expand-all"
                          @click:append-inner="openExpandedText('Вопрос на русском', testData.questionRu, { type: 'question', lang: 'ru' })"
                        />
                      </v-window-item>
                      
                      <v-window-item value="kz">
                        <v-textarea
                          v-model="testData.questionKz"
                          label="Сұрақ қазақ тілінде"
                          :rules="[v => !!v || 'Сұрақ қазақ тілінде міндетті']"
                          :error="!testData.questionKz && showValidationErrors"
                          :error-messages="!testData.questionKz && showValidationErrors ? 'Қазақ тіліндегі сұрақ міндетті' : []"
                          required
                          variant="outlined"
                          rows="2"
                          auto-grow
                          density="compact"
                          hide-details="auto"
                          class="compact-input"
                          append-inner-icon="mdi-arrow-expand-all"
                          @click:append-inner="openExpandedText('Сұрақ қазақ тілінде', testData.questionKz, { type: 'question', lang: 'kz' })"
                        />
                      </v-window-item>
                      
                      <v-window-item value="en">
                        <v-textarea
                          v-model="testData.questionEn"
                          label="Question in English"
                          :rules="[v => !!v || 'Question in English is required']"
                          :error="!testData.questionEn && showValidationErrors"
                          :error-messages="!testData.questionEn && showValidationErrors ? 'Ағылшын тіліндегі сұрақ міндетті' : []"
                          required
                          variant="outlined"
                          rows="2"
                          auto-grow
                          density="compact"
                          hide-details="auto"
                          class="compact-input"
                          append-inner-icon="mdi-arrow-expand-all"
                          @click:append-inner="openExpandedText('Question in English', testData.questionEn, { type: 'question', lang: 'en' })"
                        />
                      </v-window-item>
                    </v-window>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12">
                <v-card variant="outlined" class="mb-2">
                  <v-card-title class="text-subtitle-2 py-1 px-3 d-flex align-center">
                    <v-icon size="small" class="mr-1">mdi-tag</v-icon>
                    {{ $t('test.topic') || 'Тема' }}
                    <v-spacer />
                    <v-btn
                      v-if="!showNewTopicForm"
                      size="x-small"
                      color="primary"
                      variant="text"
                      @click="showNewTopicForm = true"
                    >
                      <v-icon size="small" start>mdi-plus</v-icon>
                      {{ $t('admin.addNewTopic') || 'Жаңа тақырып' }}
                    </v-btn>
                  </v-card-title>
                  <v-card-text class="pt-0 pb-2 px-3">
                    <!-- Existing topics dropdown -->
                    <v-combobox
                      v-if="!showNewTopicForm"
                      v-model="testData.topic"
                      :items="topicItems"
                      item-title="title"
                      item-value="value"
                      :label="$t('test.selectTopic') || 'Тізімнен таңдаңыз'"
                      :hint="$t('admin.topicHint') || 'Тізімнен таңдаңыз немесе жаңа тақырып қосыңыз'"
                      persistent-hint
                      variant="outlined"
                      clearable
                      density="compact"
                      hide-details="auto"
                      class="compact-input"
                      return-object
                    />
                    
                    <!-- New topic form (multilingual) -->
                    <div v-if="showNewTopicForm">
                      <v-alert type="info" density="compact" variant="tonal" class="mb-2">
                        {{ $t('admin.newTopicHint') || 'Жаңа тақырыпты үш тілде енгізіңіз' }}
                      </v-alert>
                      <v-row dense>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="newTopic.ru"
                            :label="$t('admin.topicRu') || 'Тема (RU)'"
                            variant="outlined"
                            density="compact"
                            hide-details="auto"
                            class="compact-input"
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="newTopic.kz"
                            :label="$t('admin.topicKz') || 'Тақырып (KZ)'"
                            variant="outlined"
                            density="compact"
                            hide-details="auto"
                            class="compact-input"
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="newTopic.en"
                            :label="$t('admin.topicEn') || 'Topic (EN)'"
                            variant="outlined"
                            density="compact"
                            hide-details="auto"
                            class="compact-input"
                          />
                        </v-col>
                      </v-row>
                      <div class="d-flex justify-end mt-2 gap-2">
                        <v-btn
                          size="small"
                          variant="text"
                          @click="cancelNewTopic"
                        >
                          {{ $t('common.cancel') || 'Болдырмау' }}
                        </v-btn>
                        <v-btn
                          size="small"
                          color="primary"
                          variant="flat"
                          :disabled="!newTopic.ru && !newTopic.kz && !newTopic.en"
                          @click="applyNewTopic"
                        >
                          <v-icon size="small" start>mdi-check</v-icon>
                          {{ $t('common.apply') || 'Қолдану' }}
                        </v-btn>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>

              <!-- ═══════ VIDEO SECTION ═══════ -->
              <v-col cols="12">
                <v-card variant="outlined" class="mb-2 video-section-card">
                  <!-- Header with toggle -->
                  <v-card-title class="text-subtitle-1 py-2 px-4 d-flex align-center justify-space-between" style="border-bottom: 1px solid rgba(255,255,255,0.08);">
                    <div class="d-flex align-center">
                      <v-icon color="blue-lighten-1" class="mr-2" size="20">mdi-video-outline</v-icon>
                      <span style="font-size: 0.95rem; font-weight: 600;">{{ $t('test.videos') }}</span>
                    </div>
                    <v-switch
                      v-model="noVideo"
                      :label="$t('test.noVideo')"
                      color="warning"
                      density="compact"
                      hide-details
                      inset
                    />
                  </v-card-title>

                  <v-card-text class="pa-4">
                    <!-- No-video animation picker -->
                    <template v-if="noVideo">
                      <v-alert type="info" variant="tonal" density="compact" class="mb-3">
                        {{ $t('test.noVideoHint') }}
                      </v-alert>
                      <v-radio-group v-model="animationType" density="compact" hide-details inline>
                        <v-radio value="noVideo" :label="$t('test.animNoVideo')" color="primary" />
                        <v-radio value="logoMedical" :label="$t('test.animLogoMedical')" color="error" />
                      </v-radio-group>
                    </template>

                    <template v-if="!noVideo">
                      <!-- ── Current videos ── -->
                      <div v-if="editingTest && (currentVideos.video1 || currentVideos.video2)">
                        <div class="text-overline mb-3" style="letter-spacing: 1.5px; opacity: 0.6;">{{ $t('test.currentVideos') }}</div>

                        <v-row dense>
                          <!-- Video 1 -->
                          <v-col cols="12" md="6" v-if="currentVideos.video1">
                            <v-card variant="outlined" class="pa-3 rounded-lg">
                              <div class="d-flex align-center mb-2">
                                <v-icon color="primary" size="18" class="mr-2">mdi-play-circle</v-icon>
                                <span class="text-body-2 font-weight-bold">{{ $t('test.video1') }}</span>
                                <v-spacer />
                                <v-btn icon="mdi-play" size="x-small" variant="tonal" color="primary" density="compact" @click="previewVideo(currentVideos.video1)" />
                                <v-btn icon="mdi-delete-outline" size="x-small" variant="text" color="error" density="compact" class="ml-1" @click="deleteVideo('video1')" :loading="deletingVideo === 'video1'" />
                              </div>
                              <div class="text-caption text-medium-emphasis" style="word-break: break-all;">{{ currentVideos.video1 }}</div>

                              <!-- AI modify inline -->
                              <v-expansion-panels v-if="aiEnabled" variant="accordion" flat class="mt-2">
                                <v-expansion-panel bg-color="transparent" style="border: 1px solid rgba(255,255,255,0.12); border-radius: 8px !important;">
                                  <v-expansion-panel-title class="py-1 px-3" style="min-height: 36px;">
                                    <div class="d-flex align-center">
                                      <v-icon size="16" color="purple" class="mr-2">mdi-auto-fix</v-icon>
                                      <span class="text-caption font-weight-medium">{{ $t('aiSettings.aiModify') }}</span>
                                    </div>
                                  </v-expansion-panel-title>
                                  <v-expansion-panel-text>
                                    <v-text-field
                                      v-model="aiModifyPromptV1"
                                      variant="outlined"
                                      density="compact"
                                      hide-details
                                      :placeholder="$t('aiSettings.modifyPlaceholder')"
                                      class="mb-2 mt-1"
                                    />
                                    <v-btn color="purple" size="small" variant="flat" block rounded="lg" :loading="aiModifyingV1" @click="startAiModifyFor('video1')">
                                      <v-icon start size="16">mdi-auto-fix</v-icon>
                                      {{ $t('aiSettings.modifyBtn') }} ({{ $t('test.video1') }})
                                    </v-btn>
                                  </v-expansion-panel-text>
                                </v-expansion-panel>
                              </v-expansion-panels>
                            </v-card>
                          </v-col>

                          <!-- Video 2 -->
                          <v-col cols="12" md="6" v-if="currentVideos.video2">
                            <v-card variant="outlined" class="pa-3 rounded-lg">
                              <div class="d-flex align-center mb-2">
                                <v-icon color="primary" size="18" class="mr-2">mdi-play-circle-outline</v-icon>
                                <span class="text-body-2 font-weight-bold">{{ $t('test.video2') }}</span>
                                <v-spacer />
                                <v-btn icon="mdi-play" size="x-small" variant="tonal" color="primary" density="compact" @click="previewVideo(currentVideos.video2)" />
                                <v-btn icon="mdi-delete-outline" size="x-small" variant="text" color="error" density="compact" class="ml-1" @click="deleteVideo('video2')" :loading="deletingVideo === 'video2'" />
                              </div>
                              <div class="text-caption text-medium-emphasis" style="word-break: break-all;">{{ currentVideos.video2 }}</div>

                              <!-- AI modify inline -->
                              <v-expansion-panels v-if="aiEnabled" variant="accordion" flat class="mt-2">
                                <v-expansion-panel bg-color="transparent" style="border: 1px solid rgba(255,255,255,0.12); border-radius: 8px !important;">
                                  <v-expansion-panel-title class="py-1 px-3" style="min-height: 36px;">
                                    <div class="d-flex align-center">
                                      <v-icon size="16" color="purple" class="mr-2">mdi-auto-fix</v-icon>
                                      <span class="text-caption font-weight-medium">{{ $t('aiSettings.aiModify') }}</span>
                                    </div>
                                  </v-expansion-panel-title>
                                  <v-expansion-panel-text>
                                    <v-text-field
                                      v-model="aiModifyPromptV2"
                                      variant="outlined"
                                      density="compact"
                                      hide-details
                                      :placeholder="$t('aiSettings.modifyPlaceholder')"
                                      class="mb-2 mt-1"
                                    />
                                    <v-btn color="purple" size="small" variant="flat" block rounded="lg" :loading="aiModifyingV2" @click="startAiModifyFor('video2')">
                                      <v-icon start size="16">mdi-auto-fix</v-icon>
                                      {{ $t('aiSettings.modifyBtn') }} ({{ $t('test.video2') }})
                                    </v-btn>
                                  </v-expansion-panel-text>
                                </v-expansion-panel>
                              </v-expansion-panels>
                            </v-card>
                          </v-col>
                        </v-row>
                      </div>

                      <!-- ── Upload new videos ── -->
                      <div class="text-overline mt-4 mb-3" style="letter-spacing: 1.5px; opacity: 0.6;">{{ $t('test.uploadNewVideos') }}</div>
                      <v-row dense>
                        <v-col cols="12" md="6">
                          <v-file-input
                            v-model="testData.video1"
                            :label="$t('test.video1')"
                            accept="video/*"
                            variant="outlined"
                            prepend-icon="mdi-video"
                            show-size
                            density="compact"
                            hide-details="auto"
                            class="compact-input"
                          />
                          <v-chip v-if="testData.video1" color="success" size="x-small" class="mt-1">
                            <v-icon start size="x-small">mdi-check</v-icon>
                            {{ testData.video1[0]?.name }}
                          </v-chip>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-file-input
                            v-model="testData.video2"
                            :label="$t('test.video2')"
                            accept="video/*"
                            variant="outlined"
                            prepend-icon="mdi-video-plus"
                            show-size
                            density="compact"
                            hide-details="auto"
                            class="compact-input"
                          />
                          <v-chip v-if="testData.video2" color="info" size="x-small" class="mt-1">
                            <v-icon start size="x-small">mdi-check</v-icon>
                            {{ testData.video2[0]?.name }}
                          </v-chip>
                        </v-col>
                      </v-row>

                      <!-- ── AI Generate new video ── -->
                      <v-expansion-panels v-if="aiEnabled" variant="accordion" flat class="mt-4">
                        <v-expansion-panel bg-color="transparent" style="border: 1px solid rgba(255,255,255,0.12); border-radius: 10px !important;">
                          <v-expansion-panel-title class="py-2 px-3" style="min-height: 40px;">
                            <div class="d-flex align-center">
                              <v-icon size="18" color="success" class="mr-2">mdi-creation</v-icon>
                              <span class="text-body-2 font-weight-medium">{{ $t('aiSettings.aiGenerateNew') }}</span>
                            </div>
                          </v-expansion-panel-title>
                          <v-expansion-panel-text>
                            <v-textarea
                              v-model="aiGeneratePrompt"
                              :label="$t('aiSettings.prompt')"
                              variant="outlined"
                              density="compact"
                              rows="2"
                              auto-grow
                              hide-details="auto"
                              class="compact-input mb-2"
                              :placeholder="$t('aiSettings.promptPlaceholder')"
                            />
                            <v-btn
                              color="green"
                              size="small"
                              variant="flat"
                              block
                              rounded="lg"
                              :loading="aiGenerating"
                              :disabled="!aiGeneratePrompt"
                              @click="startAiGenerate"
                            >
                              <v-icon start size="16">mdi-creation</v-icon>
                              {{ $t('aiSettings.generateBtn') }}
                            </v-btn>
                          </v-expansion-panel-text>
                        </v-expansion-panel>
                      </v-expansion-panels>

                      <!-- AI Job Status -->
                      <v-alert
                        v-if="aiEnabled && aiJobStatus"
                        :type="aiJobStatus.type"
                        density="compact"
                        class="mt-3"
                        closable
                        @click:close="aiJobStatus = null"
                      >
                        <div class="d-flex align-center justify-space-between flex-wrap ga-2">
                          <span class="text-body-2">{{ aiJobStatus.message }}</span>
                          <div class="d-flex ga-1">
                            <v-btn
                              v-if="aiJobStatus.jobId && aiJobStatus.status === 'running'"
                              size="x-small" variant="outlined" rounded="lg"
                              @click="pollAiJob(aiJobStatus.jobId)" :loading="aiPolling"
                            >
                              <v-icon start size="14">mdi-refresh</v-icon> {{ $t('aiSettings.checkStatus') }}
                            </v-btn>
                            <v-btn
                              v-if="aiJobStatus.status === 'completed'"
                              size="x-small" color="success" variant="flat" rounded="lg"
                              @click="applyAiVideo(aiJobStatus.jobId)" :loading="aiApplying"
                            >
                              <v-icon start size="14">mdi-check</v-icon> {{ $t('aiSettings.applyBtn') }}
                            </v-btn>
                          </div>
                        </div>
                      </v-alert>
                    </template>
                  </v-card-text>
                </v-card>
              </v-col>

              <!-- Answer options -->
              <v-col cols="12">
                <v-card variant="outlined" class="mb-2">
                  <v-card-title class="text-subtitle-1 py-1 px-3">
                    {{ $t('test.answerOptions') || 'Варианты ответов' }}
                  </v-card-title>
                  
                  <v-card-text class="pt-0 pb-2 px-3">
                    <v-window v-model="currentLanguage">
                      <!-- Russian Answers -->
                      <v-window-item value="ru">
                        <v-row v-for="(option, index) in testData.options" :key="'ru-' + index" class="mb-1" dense align="center">
                          <v-col cols="8" md="9">
                            <v-textarea
                              v-model="option.textRu"
                              :label="`Вариант ${index + 1} (RU)`"
                              variant="outlined"
                              density="compact"
                              rows="1"
                              auto-grow
                              required
                              hide-details="auto"
                              class="compact-input"
                              append-inner-icon="mdi-arrow-expand-all"
                              @click:append-inner="openExpandedText(`Вариант ${index + 1} (RU)`, option.textRu, { type: 'option', lang: 'ru', index })"
                            />
                          </v-col>
                          <v-col cols="2" md="1" class="d-flex align-center justify-center">
                            <v-checkbox
                              v-model="option.isCorrect"
                              label="✓"
                              :error="!hasCorrectAnswer && showValidationErrors"
                              @change="handleCorrectAnswerChange(index)"
                              density="compact"
                              hide-details
                              class="compact-checkbox"
                            />
                          </v-col>
                          <v-col cols="2" md="2" class="d-flex align-center">
                            <v-tooltip :text="$t('admin.deleteOption') || 'Нұсқаны өшіру'">
                              <template v-slot:activator="{ props }">
                                <v-btn
                                  v-bind="props"
                                  icon="mdi-delete"
                                  size="x-small"
                                  variant="text"
                                  color="error"
                                  :disabled="testData.options.length <= 2"
                                  @click="removeOption(index)"
                                />
                              </template>
                            </v-tooltip>
                          </v-col>
                        </v-row>
                      </v-window-item>
                      
                      <!-- Kazakh Answers -->
                      <v-window-item value="kz">
                        <v-row v-for="(option, index) in testData.options" :key="'kz-' + index" class="mb-1" dense align="center">
                          <v-col cols="8" md="9">
                            <v-textarea
                              v-model="option.textKz"
                              :label="`Нұсқа ${index + 1} (KZ)`"
                              :rules="[v => !!v || 'Нұсқа бос болмауы керек']"
                              :error="!option.textKz && showValidationErrors"
                              variant="outlined"
                              density="compact"
                              rows="1"
                              auto-grow
                              hide-details="auto"
                              class="compact-input"
                              append-inner-icon="mdi-arrow-expand-all"
                              @click:append-inner="openExpandedText(`Нұсқа ${index + 1} (KZ)`, option.textKz, { type: 'option', lang: 'kz', index })"
                            />
                          </v-col>
                          <v-col cols="2" md="1" class="d-flex align-center justify-center">
                            <v-checkbox
                              v-model="option.isCorrect"
                              label="✓"
                              :error="!hasCorrectAnswer && showValidationErrors"
                              @change="handleCorrectAnswerChange(index)"
                              density="compact"
                              hide-details
                              class="compact-checkbox"
                            />
                          </v-col>
                          <v-col cols="2" md="2" class="d-flex align-center">
                            <v-tooltip :text="$t('admin.deleteOption') || 'Нұсқаны өшіру'">
                              <template v-slot:activator="{ props }">
                                <v-btn
                                  v-bind="props"
                                  icon="mdi-delete"
                                  size="x-small"
                                  variant="text"
                                  color="error"
                                  :disabled="testData.options.length <= 2"
                                  @click="removeOption(index)"
                                />
                              </template>
                            </v-tooltip>
                          </v-col>
                        </v-row>
                      </v-window-item>
                      
                      <!-- English Answers -->
                      <v-window-item value="en">
                        <v-row v-for="(option, index) in testData.options" :key="'en-' + index" class="mb-1" dense align="center">
                          <v-col cols="8" md="9">
                            <v-textarea
                              v-model="option.textEn"
                              :label="`Option ${index + 1} (EN)`"
                              :rules="[v => !!v || 'Option cannot be empty']"
                              :error="!option.textEn && showValidationErrors"
                              variant="outlined"
                              density="compact"
                              rows="1"
                              auto-grow
                              hide-details="auto"
                              class="compact-input"
                              append-inner-icon="mdi-arrow-expand-all"
                              @click:append-inner="openExpandedText(`Option ${index + 1} (EN)`, option.textEn, { type: 'option', lang: 'en', index })"
                            />
                          </v-col>
                          <v-col cols="2" md="1" class="d-flex align-center justify-center">
                            <v-checkbox
                              v-model="option.isCorrect"
                              label="✓"
                              :error="!hasCorrectAnswer && showValidationErrors"
                              @change="handleCorrectAnswerChange(index)"
                              density="compact"
                              hide-details
                              class="compact-checkbox"
                            />
                          </v-col>
                          <v-col cols="2" md="2" class="d-flex align-center">
                            <v-tooltip text="Remove option">
                              <template v-slot:activator="{ props }">
                                <v-btn
                                  v-bind="props"
                                  icon="mdi-delete"
                                  size="x-small"
                                  variant="text"
                                  color="error"
                                  :disabled="testData.options.length <= 2"
                                  @click="removeOption(index)"
                                />
                              </template>
                            </v-tooltip>
                          </v-col>
                        </v-row>
                      </v-window-item>
                    </v-window>

                    <!-- Validation error for no correct answer -->
                    <v-alert
                      v-if="!hasCorrectAnswer && showValidationErrors"
                      type="error"
                      variant="tonal"
                      density="compact"
                      class="mt-2 py-1"
                      style="font-size: 0.8rem;"
                    >
                      {{ $t('admin.pickCorrectAnswerWarning') || 'Дұрыс жауапты таңдаңыз' }}
                    </v-alert>

                    <!-- Action buttons -->
                    <div class="mt-2 d-flex gap-2 flex-wrap">
                      <v-btn
                        v-if="testData.options.length < 6"
                        color="secondary"
                        variant="outlined"
                        size="x-small"
                        @click="addOption"
                      >
                        <v-icon class="mr-1" size="x-small">mdi-plus</v-icon>
                        <span class="text-caption">{{ $t('test.addOption') || 'Нұсқа қосу' }}</span>
                      </v-btn>

                      <v-btn
                        v-if="hasEmptyOptions"
                        color="warning"
                        variant="outlined"
                        size="x-small"
                        @click="removeEmptyOptions"
                      >
                        <v-icon class="mr-1" size="x-small">mdi-delete-sweep</v-icon>
                        <span class="text-caption">{{ $t('test.removeEmptyOptions') || 'Бос нұсқаларды өшіру' }}</span>
                      </v-btn>

                      <v-btn
                        v-if="testData.options.length > 2"
                        color="error"
                        variant="text"
                        size="x-small"
                        @click="clearAllOptions"
                      >
                        <v-icon class="mr-1" size="x-small">mdi-trash-can</v-icon>
                        <span class="text-caption">{{ $t('test.clearAllOptions') || 'Барлығын тазалау' }}</span>
                      </v-btn>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>

              <!-- Explanation -->
              <v-col cols="12">
                <v-card variant="outlined" class="mb-2">
                  <v-card-title class="text-subtitle-1 py-1 px-3">
                    {{ $t('test.explanation') || 'Объяснение' }}
                  </v-card-title>
                  
                  <v-card-text class="pt-0 pb-2 px-3">
                    <v-window v-model="currentLanguage">
                      <v-window-item value="ru">
                        <v-textarea
                          v-model="testData.explanationRu"
                          label="Объяснение (Русский)"
                          variant="outlined"
                          rows="2"
                          auto-grow
                          density="compact"
                          hint="Объяснение правильного ответа"
                          hide-details="auto"
                          class="compact-input"
                          append-inner-icon="mdi-arrow-expand-all"
                          @click:append-inner="openExpandedText('Объяснение (Русский)', testData.explanationRu, { type: 'explanation', lang: 'ru' })"
                        />
                      </v-window-item>
                      
                      <v-window-item value="kz">
                        <v-textarea
                          v-model="testData.explanationKz"
                          label="Түсініктеме (Қазақша)"
                          variant="outlined"
                          rows="2"
                          auto-grow
                          density="compact"
                          hint="Дұрыс жауаптың түсініктемесі"
                          hide-details="auto"
                          class="compact-input"
                          append-inner-icon="mdi-arrow-expand-all"
                          @click:append-inner="openExpandedText('Түсініктеме (Қазақша)', testData.explanationKz, { type: 'explanation', lang: 'kz' })"
                        />
                      </v-window-item>
                      
                      <v-window-item value="en">
                        <v-textarea
                          v-model="testData.explanationEn"
                          label="Explanation (English)"
                          variant="outlined"
                          rows="2"
                          auto-grow
                          density="compact"
                          hint="Explanation of the correct answer"
                          hide-details="auto"
                          class="compact-input"
                          append-inner-icon="mdi-arrow-expand-all"
                          @click:append-inner="openExpandedText('Explanation (English)', testData.explanationEn, { type: 'explanation', lang: 'en' })"
                        />
                      </v-window-item>
                    </v-window>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <!-- Validation Errors Alert -->
        <v-alert
          v-if="hasValidationErrors"
          type="error"
          class="mx-4 mb-2 py-2"
          variant="tonal"
          density="compact"
        >
          <div class="text-caption font-weight-bold mb-1">{{ $t('admin.validationErrors') || 'Ошибки валидации:' }}</div>
          <ul class="text-caption pl-4">
            <li v-for="error in currentTestErrors" :key="error">{{ error }}</li>
          </ul>
        </v-alert>

        <v-card-actions class="pa-2">
          <v-spacer />
          <v-btn
            variant="text"
            size="small"
            @click="closeTestDialog"
          >
            {{ $t('common.cancel') || 'Отмена' }}
          </v-btn>
          <v-btn
            color="primary"
            :loading="saving"
            :disabled="!isFormValid || hasValidationErrors"
            @click="saveTest"
            size="small"
          >
            <template v-if="uploadingVideo">
              <v-icon class="mr-2" size="small">mdi-cloud-upload</v-icon>
              {{ $t('common.uploading') || 'Загрузка...' }}
            </template>
            <template v-else>
              {{ $t('common.save') || 'Сохранить' }}
            </template>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Close Test Dialog Confirmation -->
    <v-dialog v-model="closeConfirmDialog" max-width="450px">
      <v-card>
        <v-card-title class="text-h6 d-flex align-center">
          <v-icon class="mr-2" color="warning">mdi-alert-circle</v-icon>
          {{ $t('common.unsavedChanges') }}
        </v-card-title>
        <v-card-text class="py-4">
          <p class="text-body-1 mb-2">{{ $t('common.unsavedChangesMessage') }}</p>
          <p class="text-body-2 text-medium-emphasis">{{ $t('common.howToContinue') }}</p>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-btn
            variant="text"
            color="grey"
            @click="closeConfirmDialog = false"
          >
            {{ $t('common.back') }}
          </v-btn>
          <v-spacer />
          <v-btn
            variant="outlined"
            color="error"
            @click="closeWithoutSaving"
          >
            <v-icon start>mdi-close</v-icon>
            {{ $t('common.discard') }}
          </v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            :loading="saving"
            :disabled="!isFormValid || hasValidationErrors"
            @click="saveAndClose"
          >
            <v-icon start>mdi-content-save</v-icon>
            {{ $t('common.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete confirmation dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="text-h5">
          {{ $t('admin.confirmDelete') || 'Подтвердите удаление' }}
        </v-card-title>
        <v-card-text>
          {{ $t('admin.confirmDeleteTest') || 'Вы уверены, что хотите удалить этот тест?' }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="deleteDialog = false">
            {{ $t('common.cancel') || 'Отмена' }}
          </v-btn>
          <v-btn
            color="error"
            :loading="deleting"
            @click="confirmDelete"
          >
            {{ $t('common.delete') || 'Удалить' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete All Tests confirmation dialog -->
    <v-dialog v-model="deleteAllDialog" max-width="500px">
      <v-card>
        <v-card-title class="text-h5 d-flex align-center">
          <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
          {{ $t('admin.confirmDeleteAll') || 'Барлық тесттерді өшіру' }}
        </v-card-title>
        <v-card-text>
          <v-alert type="warning" variant="tonal" class="mb-4">
            <div class="font-weight-bold">{{ $t('admin.dangerZone') || 'Қауіпті аймақ!' }}</div>
            <div class="mt-2">{{ $t('admin.deleteAllWarning') || 'Бұл әрекет барлық тесттерді толығымен жояды және оны қайтару мүмкін емес!' }}</div>
          </v-alert>
          
          <div class="mb-3">
            <strong>{{ $t('admin.testsToDelete') || 'Өшірілетін тесттер саны:' }}</strong> 
            <v-chip color="error" size="small" class="ml-2">
              {{ filteredTests?.length || 0 }}
            </v-chip>
          </div>
          
          <v-text-field
            v-model="confirmationText"
            :label="$t('admin.deleteConfirmLabel') || 'Растау үшін ӨШІРУ деп жазыңыз'"
            :hint="$t('admin.deleteConfirmHint') || 'Дәлел ретінде ӨШІРУ сөзін жазыңыз'"
            variant="outlined"
            density="compact"
            persistent-hint
            :error="confirmationText && confirmationText !== 'ӨШІРУ'"
            :error-messages="confirmationText && confirmationText !== 'ӨШІРУ' ? ($t('admin.deleteConfirmError') || 'ӨШІРУ деп жазыңыз') : []"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="closeDeleteAllDialog">
            {{ $t('common.cancel') || 'Бас тарту' }}
          </v-btn>
          <v-btn
            color="error"
            :loading="deletingAll"
            :disabled="confirmationText !== 'ӨШІРУ'"
            @click="confirmDeleteAll"
          >
            <v-icon class="mr-1">mdi-delete-forever</v-icon>
            {{ $t('admin.deleteAllConfirm') || 'Барлығын өшіру' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Untaken Tests Dialog -->
    <v-dialog v-model="untakenDialog" max-width="1100px" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon class="mr-2" color="orange">mdi-test-tube-off</v-icon>
            <span>{{ $t('admin.untakenTests') || 'Тапсырылмаған тесттер' }}</span>
            <v-chip class="ml-3" size="small" color="orange" variant="tonal">
              {{ untakenTests.length }}
            </v-chip>
          </div>
          <v-btn icon="mdi-close" variant="text" @click="untakenDialog = false" />
        </v-card-title>

        <v-card-text style="max-height: 70vh;">
          <div v-if="untakenLoading" class="text-center py-8">
            <v-progress-circular indeterminate color="primary" size="48" />
            <div class="mt-2">{{ $t('common.loading') || 'Жүктелуде...' }}</div>
          </div>

          <div v-else-if="untakenTests.length === 0" class="text-center py-8">
            <v-icon color="success" size="64">mdi-check-circle</v-icon>
            <div class="mt-2 text-h6">{{ $t('admin.allTestsTaken') || 'Барлық тесттер тапсырылған' }}</div>
          </div>

          <div v-else>
            <v-alert type="info" variant="tonal" density="compact" class="mb-3">
              {{ $t('admin.untakenHint') || 'Бұл тесттер ешқашан тапсырылмаған. Әрбір тестті өңдеп немесе өшіруге болады.' }}
            </v-alert>

            <v-data-table
              :headers="untakenHeaders"
              :items="untakenTests"
              :items-per-page="25"
              :items-per-page-options="[10, 25, 50, -1]"
              :items-per-page-text="$t('common.itemsPerPage') || 'Беттегі элементтер саны:'"
              density="compact"
            >
              <template v-slot:item.id="{ item }">
                <span class="text-caption">#{{ item.id }}</span>
              </template>
              <template v-slot:item.topic="{ item }">
                <v-chip v-if="item.topic" size="x-small" color="primary" variant="tonal">
                  {{ formatTopicLabel(item.topic) }}
                </v-chip>
                <v-chip v-else size="x-small" color="error" variant="tonal">
                  {{ $t('admin.noTopic') || 'тақырып жоқ' }}
                </v-chip>
              </template>
              <template v-slot:item.question="{ item }">
                <div class="text-body-2" style="max-width: 360px;">
                  {{ item.questionKz || item.questionRu || '—' }}
                </div>
              </template>
              <template v-slot:item.issues="{ item }">
                <div class="d-flex flex-wrap gap-1">
                  <v-chip v-if="item.issues.includes('emptyTopic')" size="x-small" color="error" variant="tonal">
                    <v-icon start size="x-small">mdi-alert</v-icon>
                    {{ $t('admin.issueEmptyTopic') || 'тақырып жоқ' }}
                  </v-chip>
                  <v-chip v-if="item.issues.includes('noVideo')" size="x-small" color="warning" variant="tonal">
                    <v-icon start size="x-small">mdi-video-off</v-icon>
                    {{ $t('admin.issueNoVideo') || 'видео жоқ' }}
                  </v-chip>
                  <v-chip v-if="item.issues.length === 0" size="x-small" color="success" variant="tonal">
                    <v-icon start size="x-small">mdi-check</v-icon>
                    OK
                  </v-chip>
                </div>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-btn
                  icon="mdi-pencil"
                  size="small"
                  color="primary"
                  variant="text"
                  @click="editFromUntaken(item)"
                />
                <v-btn
                  icon="mdi-delete"
                  size="small"
                  color="error"
                  variant="text"
                  @click="deleteFromUntaken(item)"
                />
              </template>
            </v-data-table>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-btn @click="loadUntakenTests" :loading="untakenLoading">
            <v-icon class="mr-1">mdi-refresh</v-icon>
            {{ $t('common.refresh') || 'Жаңарту' }}
          </v-btn>
          <v-spacer />
          <v-btn variant="text" @click="untakenDialog = false">
            {{ $t('common.close') || 'Жабу' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Test Statistics Dialog -->
    <v-dialog v-model="statsDialog" max-width="600px">
      <v-card>
        <v-card-title class="text-h5">
          <v-icon class="mr-2">mdi-chart-line</v-icon>
          {{ $t('admin.testStatistics') || 'Статистика теста' }}
        </v-card-title>
        
        <v-card-text v-if="currentTestStats">
          <div class="mb-4">
            <h6 class="text-h6 mb-2">{{ currentTestStats.questionRu || currentTestStats.question }}</h6>
            <div class="d-flex gap-2 mb-4">
              <v-chip v-if="currentTestStats.topic" color="primary" size="small" variant="tonal">
                {{ formatTopicLabel(currentTestStats.topic) }}
              </v-chip>
            </div>
          </div>

          <v-divider class="mb-4" />

          <div v-if="statsLoading" class="text-center py-4">
            <v-progress-circular indeterminate color="primary" />
            <p class="mt-2">{{ $t('common.loading') || 'Загрузка...' }}</p>
          </div>

          <div v-else-if="currentTestStats.statistics">
            <v-row>
              <v-col cols="12" md="6">
                <v-card variant="outlined" class="pa-3">
                  <div class="text-center">
                    <div class="text-h4 text-primary">{{ currentTestStats.statistics.totalAttempts || 0 }}</div>
                    <div class="text-subtitle-1">{{ $t('admin.totalAttempts') || 'Всего попыток' }}</div>
                  </div>
                </v-card>
              </v-col>
              
              <v-col cols="12" md="6">
                <v-card variant="outlined" class="pa-3">
                  <div class="text-center">
                    <div class="text-h4 text-success">{{ currentTestStats.statistics.averageScore || 0 }}%</div>
                    <div class="text-subtitle-1">{{ $t('admin.averageScore') || 'Средний балл' }}</div>
                  </div>
                </v-card>
              </v-col>

              <v-col cols="12" md="6">
                <v-card variant="outlined" class="pa-3">
                  <div class="text-center">
                    <div class="text-h4 text-success">{{ currentTestStats.statistics.correctAnswers || 0 }}</div>
                    <div class="text-subtitle-1">{{ $t('admin.correctAnswers') || 'Правильных ответов' }}</div>
                  </div>
                </v-card>
              </v-col>

              <v-col cols="12" md="6">
                <v-card variant="outlined" class="pa-3">
                  <div class="text-center">
                    <div class="text-h4 text-error">{{ currentTestStats.statistics.incorrectAnswers || 0 }}</div>
                    <div class="text-subtitle-1">{{ $t('admin.incorrectAnswers') || 'Неправильных ответов' }}</div>
                  </div>
                </v-card>
              </v-col>

              <v-col cols="12">
                <v-card variant="outlined" class="pa-3">
                  <h6 class="text-h6 mb-3">{{ $t('admin.successRate') || 'Процент успешности' }}</h6>
                  <v-progress-linear
                    :model-value="currentTestStats.statistics.successRate || 0"
                    height="20"
                    color="success"
                    class="mb-2"
                  >
                    <strong>{{ Math.round(currentTestStats.statistics.successRate || 0) }}%</strong>
                  </v-progress-linear>
                </v-card>
              </v-col>

              <v-col cols="12" v-if="currentTestStats.statistics.recentResults && currentTestStats.statistics.recentResults.length > 0">
                <v-card variant="outlined" class="pa-3">
                  <h6 class="text-h6 mb-3">{{ $t('admin.recentResults') || 'Последние результаты' }}</h6>
                  <v-list density="compact">
                    <v-list-item
                      v-for="result in currentTestStats.statistics.recentResults"
                      :key="result.id"
                      class="px-0"
                    >
                      <template v-slot:prepend>
                        <v-icon :color="result.isCorrect ? 'success' : 'error'">
                          {{ result.isCorrect ? 'mdi-check-circle' : 'mdi-close-circle' }}
                        </v-icon>
                      </template>
                      
                      <v-list-item-title>{{ result.user?.fio || ($t('admin.anonymousUser') || 'Анонимный пользователь') }}</v-list-item-title>
                      <v-list-item-subtitle>
                        {{ new Date(result.createdAt).toLocaleDateString() }} - 
                        {{ result.score }}% 
                        ({{ result.mode }})
                      </v-list-item-subtitle>
                    </v-list-item>
                  </v-list>
                </v-card>
              </v-col>
            </v-row>
          </div>

          <div v-else class="text-center py-4">
            <v-icon size="64" color="grey">mdi-chart-line-variant</v-icon>
            <p class="text-subtitle-1 mt-2">{{ $t('admin.noStatisticsData') || 'Нет данных статистики' }}</p>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="statsDialog = false">
            {{ $t('common.close') || 'Закрыть' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Import Dialog -->
    <v-dialog v-model="importDialog" max-width="720px" persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2" color="primary">mdi-upload</v-icon>
          {{ $t('admin.importTests') || 'Импорт тестов' }}
        </v-card-title>
        <v-card-text>
          <v-alert type="info" variant="tonal" class="mb-3">
            <div class="text-body-2 mb-2">{{ $t('admin.importSupportedFormats') || 'Поддерживаются форматы: .txt, .xlsx, .json' }}</div>
            <div class="text-caption">{{ $t('admin.importMultipleHint') || 'Можно выбрать несколько файлов одновременно' }}</div>
          </v-alert>

          <!-- Multiple File Input -->
          <v-file-input
            v-model="importFiles"
            :label="$t('admin.importFilesLabel') || 'Файлы для импорта'"
            accept=".txt,.xlsx,.xls,.json"
            prepend-inner-icon="mdi-paperclip"
            :disabled="importLoading"
            multiple
            show-size
            counter
            variant="outlined"
            density="compact"
          />

          <!-- Selected Files List -->
          <div v-if="importFiles && importFiles.length > 0" class="mt-3">
            <v-card variant="outlined">
              <v-card-title class="text-h6 pa-3">
                <v-icon class="mr-2" size="small">mdi-file-multiple</v-icon>
                {{ $t('admin.importSelectedFiles') || 'Выбранные файлы' }} ({{ importFiles.length }})
              </v-card-title>
              <v-card-text class="pt-0">
                <v-list density="compact">
                  <v-list-item
                    v-for="(file, index) in importFiles"
                    :key="index"
                    class="px-0"
                  >
                    <template v-slot:prepend>
                      <v-icon size="small" :color="getFileTypeColor(file.name)">
                        {{ getFileTypeIcon(file.name) }}
                      </v-icon>
                    </template>
                    <v-list-item-title class="text-body-2">
                      {{ file.name }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      {{ formatFileSize(file.size) }}
                    </v-list-item-subtitle>
                    <template v-slot:append>
                      <v-btn
                        icon="mdi-close"
                        size="small"
                        variant="text"
                        color="error"
                        :disabled="importLoading"
                        @click="removeFile(index)"
                      />
                    </template>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </div>

          <!-- Progress Bar -->
          <div v-if="importLoading" class="mt-3">
            <div class="d-flex align-center justify-space-between mb-2">
              <span class="text-body-2">{{ importProgressText }}</span>
              <span class="text-caption">{{ importProgress.current }} / {{ importProgress.total }}</span>
            </div>
            <v-progress-linear
              :model-value="(importProgress.current / importProgress.total) * 100"
              color="primary"
              height="6"
            />
          </div>

          <!-- Format Help -->
          <v-expansion-panels variant="accordion" class="mt-3">
            <v-expansion-panel>
              <v-expansion-panel-title class="text-body-2">
                <v-icon class="mr-2" size="small">mdi-help-circle</v-icon>
                {{ $t('admin.importFileFormatsTitle') || 'Форматы файлов' }}
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <div class="text-caption" v-html="$t('admin.importFileFormatsHtml') || '<strong>TXT формат:</strong><br>Q: вопрос<br>A: вариант ответа<br>A*: правильный вариант<br>CAT: категория (опционально)<br>EXP: пояснение (опционально)<br><em>(пустая строка — разделитель тестов)</em><br><br><strong>Структура папок:</strong><br>Тесты/BAZA_PDD/Вопросы TXT/A/file1.txt<br>Тесты/BAZA_PDD/Вопросы TXT/B/file2.txt<br><em>Категория определяется по имени папки</em>'" />
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            variant="text"
            @click="closeImportDialog"
            :disabled="importLoading"
          >
            {{ importLoading ? ($t('common.cancel') || 'Бас тарту') : ($t('common.close') || 'Жабу') }}
          </v-btn>
          <v-btn
            color="primary"
            @click="doMultipleImport"
            :loading="importLoading"
            :disabled="!importFiles || importFiles.length === 0"
          >
            <v-icon class="mr-2">mdi-upload</v-icon>
            {{ $t('admin.importTests') || 'Импорт' }} ({{ importFiles?.length || 0 }} {{ $t('admin.filesUnit') || 'файлов' }})
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Auto Clean Dialog -->
    <v-dialog v-model="autoCleanDialog" max-width="700px" persistent>
      <v-card>
        <v-card-title class="text-h5">
          <v-icon class="mr-2" color="warning">mdi-auto-fix</v-icon>
          {{ $t('admin.autoClean') || 'Автоматты тазалау' }}
        </v-card-title>

        <v-card-text>
          <v-alert type="warning" variant="tonal" class="mb-4">
            <div class="text-body-1 mb-2">
              <v-icon class="mr-1">mdi-alert-circle</v-icon>
              <strong>{{ $t('admin.attention') || 'Назар аударыңыз!' }}</strong>
            </div>
            <div class="text-body-2">
              {{ $t('admin.autoCleanIntro') || 'Бұл функция барлық қателі тесттердегі бос өрістерді автоматты түрде тазалайды:' }}
            </div>
            <ul class="text-body-2 mt-2">
              <li>{{ $t('admin.autoCleanRemoveEmptyOptions') || 'Бос жауап нұсқаларын өшіреді' }}</li>
              <li>{{ $t('admin.autoCleanRemoveEmptyQuestions') || 'Бос сұрақтар мен түсіндірмелерді өшіреді' }}</li>
              <li>{{ $t('admin.autoCleanRemoveNoCorrect') || 'Дұрыс жауабы жоқ тесттерді жояды' }}</li>
              <li>{{ $t('admin.autoCleanIrreversible') || 'Қайтарылмайтын операция!' }}</li>
            </ul>
          </v-alert>

          <div v-if="testsWithErrors.length > 0" class="mb-4">
            <h6 class="text-h6 mb-3">
              {{ $t('admin.testsToClean') || 'Тазаланатын тесттер:' }} {{ testsWithErrors.length }}
            </h6>
            
            <!-- Preview of tests to clean -->
            <v-card variant="outlined" class="pa-3" max-height="300px" style="overflow-y: auto;">
              <v-list density="compact">
                <v-list-item
                  v-for="(test, index) in testsWithErrors.slice(0, 10)"
                  :key="test.id"
                  class="px-0"
                >
                  <template v-slot:prepend>
                    <v-avatar size="small" color="error">
                      {{ index + 1 }}
                    </v-avatar>
                  </template>
                  
                  <v-list-item-title class="text-body-2">
                    ID: {{ test.id }} - {{ test.questionRu || test.question || ($t('admin.emptyQuestion') || 'Бос сұрақ') }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-caption">
                    <v-chip v-if="test.topic" size="x-small" color="primary" class="mr-1">{{ formatTopicLabel(test.topic) }}</v-chip>
                    {{ $t('admin.errorPrefix') || 'Қате:' }} {{ getErrorDescription(test) }}
                  </v-list-item-subtitle>
                </v-list-item>
              </v-list>

              <div v-if="testsWithErrors.length > 10" class="text-center mt-2">
                <v-chip size="small" variant="tonal">
                  {{ $t('admin.andMore', { n: testsWithErrors.length - 10 }) || `... тағы ${testsWithErrors.length - 10} тест` }}
                </v-chip>
              </div>
            </v-card>
          </div>

          <div class="mb-3">
            <v-checkbox
              v-model="autoCleanOptions.removeEmptyQuestions"
              :label="$t('admin.autoCleanOptRemoveEmptyQuestions') || 'Бос сұрақтарды өшіру'"
              density="compact"
            />
            <v-checkbox
              v-model="autoCleanOptions.removeEmptyAnswers"
              :label="$t('admin.autoCleanOptRemoveEmptyAnswers') || 'Бос жауап нұсқаларын өшіру'"
              density="compact"
            />
            <v-checkbox
              v-model="autoCleanOptions.removeTestsWithoutCorrectAnswer"
              :label="$t('admin.autoCleanOptRemoveNoCorrect') || 'Дұрыс жауабы жоқ тесттерді өшіру'"
              density="compact"
            />
          </div>

          <!-- Confirmation -->
          <v-text-field
            v-model="autoCleanConfirmation"
            :label="$t('admin.autoCleanConfirmLabel') || 'Растау үшін ТАЗАЛАУ деп жазыңыз'"
            :hint="$t('admin.autoCleanConfirmHint') || 'Дәлел ретінде ТАЗАЛАУ сөзін жазыңыз'"
            variant="outlined"
            density="compact"
            persistent-hint
            :error="autoCleanConfirmation && autoCleanConfirmation !== 'ТАЗАЛАУ'"
            :error-messages="autoCleanConfirmation && autoCleanConfirmation !== 'ТАЗАЛАУ' ? ($t('admin.autoCleanConfirmError') || 'ТАЗАЛАУ деп жазыңыз') : []"
          />
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="closeAutoCleanDialog">
            {{ $t('common.cancel') || 'Бас тарту' }}
          </v-btn>
          <v-btn
            color="warning"
            :loading="autoCleanLoading"
            :disabled="autoCleanConfirmation !== 'ТАЗАЛАУ' || testsWithErrors.length === 0"
            @click="executeAutoClean"
          >
            <v-icon class="mr-1">mdi-auto-fix</v-icon>
            {{ $t('admin.startCleaning') || 'Тазалауды бастау' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Topic Editor Dialog -->
    <v-dialog v-model="topicEditorDialog" max-width="800px" persistent>
      <v-card>
        <v-card-title class="bg-teal d-flex align-center">
          <v-icon class="mr-2">mdi-tag-multiple</v-icon>
          {{ $t('admin.topicEditor') || 'Тақырыптарды редакциялау' }}
          <v-spacer />
          <v-btn icon variant="text" @click="topicEditorDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-alert type="info" variant="tonal" density="compact" class="mb-4">
            {{ $t('admin.topicEditorHint') || 'Тақырыптарды қосу, өзгерту немесе жою. Өзгертулер барлық тесттерге қолданылады.' }}
          </v-alert>

          <!-- Add new topic -->
          <v-card variant="outlined" class="mb-4">
            <v-card-title class="text-subtitle-1 py-2 px-3">
              <v-icon size="small" class="mr-1">mdi-plus</v-icon>
              {{ $t('admin.addNewTopic') || 'Жаңа тақырып қосу' }}
            </v-card-title>
            <v-card-text class="pt-0 pb-2 px-3">
              <v-row dense>
                <v-col cols="12" md="2">
                  <v-select
                    v-model="newTopicEditor.number"
                    :items="availableTopicNumbers"
                    :label="$t('admin.topicNumber') || 'Нөмір'"
                    variant="outlined"
                    density="compact"
                    hide-details="auto"
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="newTopicEditor.ru"
                    :label="$t('admin.topicRu') || 'Тема (RU)'"
                    variant="outlined"
                    density="compact"
                    hide-details="auto"
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="newTopicEditor.kz"
                    :label="$t('admin.topicKz') || 'Тақырып (KZ)'"
                    variant="outlined"
                    density="compact"
                    hide-details="auto"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="newTopicEditor.en"
                    :label="$t('admin.topicEn') || 'Topic (EN)'"
                    variant="outlined"
                    density="compact"
                    hide-details="auto"
                  />
                </v-col>
              </v-row>
              <div class="d-flex justify-end mt-2">
                <v-btn
                  color="primary"
                  size="small"
                  :disabled="(!newTopicEditor.ru && !newTopicEditor.kz && !newTopicEditor.en) || !newTopicEditor.number"
                  @click="addTopicInEditor"
                >
                  <v-icon size="small" start>mdi-plus</v-icon>
                  {{ $t('common.add') || 'Қосу' }}
                </v-btn>
              </div>
            </v-card-text>
          </v-card>

          <!-- Existing topics list -->
          <v-card variant="outlined">
            <v-card-title class="text-subtitle-1 py-2 px-3">
              <v-icon size="small" class="mr-1">mdi-format-list-bulleted</v-icon>
              {{ $t('admin.existingTopics') || 'Бар тақырыптар' }} ({{ uniqueTopics.length }})
            </v-card-title>
            <v-card-text class="pa-0">
              <v-list density="compact" max-height="400" class="overflow-y-auto">
                <v-list-item
                  v-for="(topic, index) in sortedTopics"
                  :key="index"
                  class="border-b"
                >
                  <template v-slot:prepend>
                    <v-chip size="small" color="primary" variant="flat" class="mr-2" style="min-width: 36px; justify-content: center;">
                      {{ getTopicNumber(topic) || '—' }}
                    </v-chip>
                    <v-icon size="small" color="teal">mdi-tag</v-icon>
                  </template>
                  
                  <v-list-item-title v-if="editingTopicIndex !== index">
                    <div class="d-flex flex-column">
                      <span class="text-body-2">{{ getTopicText(topic, 'ru') }}</span>
                      <span v-if="hasMultipleLanguages(topic)" class="text-caption text-medium-emphasis">
                        KZ: {{ getTopicText(topic, 'kz') }} | EN: {{ getTopicText(topic, 'en') }}
                      </span>
                    </div>
                  </v-list-item-title>

                  <!-- Edit mode -->
                  <div v-else class="py-2 w-100">
                    <v-row dense>
                      <v-col cols="12" md="2">
                        <v-select
                          v-model="editingTopic.number"
                          :items="getAvailableNumbersForEdit(topic)"
                          label="№"
                          variant="outlined"
                          density="compact"
                          hide-details
                        />
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-text-field
                          v-model="editingTopic.ru"
                          label="RU"
                          variant="outlined"
                          density="compact"
                          hide-details
                        />
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-text-field
                          v-model="editingTopic.kz"
                          label="KZ"
                          variant="outlined"
                          density="compact"
                          hide-details
                        />
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="editingTopic.en"
                          label="EN"
                          variant="outlined"
                          density="compact"
                          hide-details
                        />
                      </v-col>
                    </v-row>
                  </div>

                  <template v-slot:append>
                    <div v-if="editingTopicIndex !== index" class="d-flex gap-1">
                      <v-chip size="x-small" color="grey" variant="tonal">
                        {{ getTestCountByTopic(topic) }} {{ $t('admin.tests') || 'тест' }}
                      </v-chip>
                      <v-btn
                        icon
                        size="x-small"
                        variant="text"
                        color="primary"
                        @click="startEditTopic(index, topic)"
                        :title="$t('common.edit') || 'Өзгерту'"
                      >
                        <v-icon size="small">mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        size="x-small"
                        variant="text"
                        color="error"
                        @click="confirmDeleteTopic(topic)"
                      >
                        <v-icon size="small">mdi-delete</v-icon>
                      </v-btn>
                    </div>
                    <div v-else class="d-flex gap-1">
                      <v-btn
                        icon
                        size="x-small"
                        variant="text"
                        color="success"
                        @click="saveEditTopic"
                        :loading="topicSaving"
                      >
                        <v-icon size="small">mdi-check</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        size="x-small"
                        variant="text"
                        @click="cancelEditTopic"
                      >
                        <v-icon size="small">mdi-close</v-icon>
                      </v-btn>
                    </div>
                  </template>
                </v-list-item>

                <v-list-item v-if="uniqueTopics.length === 0">
                  <v-list-item-title class="text-center text-medium-emphasis py-4">
                    {{ $t('admin.noTopics') || 'Тақырыптар жоқ' }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="text" @click="topicEditorDialog = false">
            {{ $t('common.close') || 'Жабу' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Topic Confirmation Dialog -->
    <v-dialog v-model="deleteTopicDialog" max-width="500px">
      <v-card>
        <v-card-title class="bg-error">
          <v-icon class="mr-2">mdi-alert</v-icon>
          {{ $t('admin.confirmDelete') || 'Жоюды растау' }}
        </v-card-title>
        <v-card-text class="pt-4">
          <p>{{ $t('admin.deleteTopicWarning') || 'Бұл тақырыпты жойғыңыз келе ме?' }}</p>
          <v-alert type="warning" variant="tonal" density="compact" class="mt-2">
            <strong>{{ deletingTopic }}</strong>
            <br />
            <span class="text-caption">{{ getTestCountByTopic(deletingTopic) }} {{ $t('admin.deleteTopicWarning') || 'тест осы тақырыпта. Тесттердің тақырыбы тазаланады.' }}</span>
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="deleteTopicDialog = false">
            {{ $t('common.cancel') || 'Болдырмау' }}
          </v-btn>
          <v-btn color="error" variant="flat" @click="executeDeleteTopic" :loading="topicDeleting">
            {{ $t('common.delete') || 'Жою' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- PDF Generator Dialog -->
    <v-dialog v-model="pdfGeneratorDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="bg-success">
          <v-icon class="mr-2">mdi-file-pdf-box</v-icon>
          {{ $t('admin.pdf.title') || 'PDF Тест Генератор' }}
        </v-card-title>

        <v-card-text class="pt-4">
          <v-alert type="info" variant="tonal" density="compact" class="mb-4">
            {{ $t('admin.pdf.info') || 'Тақырып бойынша тесттерді PDF форматында экспорттау' }}
          </v-alert>

          <!-- Topic Multi-Selection -->
          <v-select
            v-model="pdfConfig.topics"
            :items="uniqueTopics"
            :label="$t('admin.pdf.selectTopics') || 'Тақырыптарды таңдау'"
            variant="outlined"
            density="comfortable"
            class="mb-3"
            multiple
            chips
            closable-chips
            clearable
          >
            <template v-slot:prepend-item>
              <v-list-item
                :title="$t('admin.pdf.selectAll') || 'Барлығын таңдау'"
                @click="toggleAllTopics"
              >
                <template v-slot:prepend>
                  <v-checkbox-btn
                    :model-value="pdfConfig.topics.length === uniqueTopics.length"
                    :indeterminate="pdfConfig.topics.length > 0 && pdfConfig.topics.length < uniqueTopics.length"
                  />
                </template>
              </v-list-item>
              <v-divider class="mt-2" />
            </template>
          </v-select>

          <!-- Language Selection -->
          <v-select
            v-model="pdfConfig.language"
            :items="languageOptions"
            :label="$t('admin.pdf.selectLanguage') || 'Тіл таңдау'"
            variant="outlined"
            density="comfortable"
            class="mb-3"
          />

          <!-- Preview count -->
          <v-alert v-if="pdfTestsCount > 0" type="success" variant="tonal" density="compact">
            <strong>{{ pdfTestsCount }}</strong> {{ $t('admin.pdf.testsFound') || 'тест табылды' }}
          </v-alert>
          <v-alert v-else-if="pdfConfig.topics.length > 0" type="warning" variant="tonal" density="compact">
            {{ $t('admin.pdf.noTestsFound') || 'Тесттер табылмады' }}
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="closePdfGeneratorDialog">
            {{ $t('common.close') || 'Жабу' }}
          </v-btn>
          <v-btn
            color="success"
            @click="generatePDF"
            :disabled="pdfConfig.topics.length === 0 || pdfTestsCount === 0"
            :loading="generatingPdf"
          >
            <v-icon class="mr-2">mdi-download</v-icon>
            {{ $t('admin.pdf.generate') || 'PDF жасау' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Duplicates Dialog -->
    <v-dialog v-model="duplicatesDialog" max-width="1000px" persistent scrollable>
      <v-card>
        <v-card-title class="bg-warning d-flex align-center">
          <v-icon class="mr-2">mdi-content-duplicate</v-icon>
          {{ $t('admin.duplicateTests') || 'Дубликат тесттер' }}
          <v-spacer></v-spacer>
          <v-chip v-if="duplicatesData" color="error" size="small" class="ml-2">
            {{ duplicatesData.totalDuplicates }} {{ $t('admin.duplicates') || 'дубликат' }}
          </v-chip>
        </v-card-title>

        <v-card-text class="pt-4">
          <!-- Loading state -->
          <div v-if="duplicatesLoading" class="text-center py-8">
            <v-progress-circular indeterminate color="warning" size="64"></v-progress-circular>
            <div class="mt-4 text-h6">{{ $t('admin.searchingDuplicates') || 'Дубликаттар іздеуде...' }}</div>
          </div>

          <!-- No duplicates -->
          <v-alert
            v-else-if="duplicatesData && duplicatesData.totalDuplicates === 0"
            type="success"
            variant="tonal"
          >
            <v-icon class="mr-2">mdi-check-circle</v-icon>
            {{ $t('admin.noDuplicatesFound') || 'Дубликат тесттер табылмады! Барлық тесттер бірегей.' }}
          </v-alert>

          <!-- Duplicates found -->
          <div v-else-if="duplicatesData && duplicatesData.groups">
            <!-- Statistics -->
            <v-row class="mb-4">
              <v-col cols="12" md="4">
                <v-card variant="outlined" class="text-center pa-3">
                  <div class="text-h4 text-primary">{{ duplicatesData.totalTests }}</div>
                  <div class="text-caption">{{ $t('admin.allTests') || 'Барлық тесттер' }}</div>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-card variant="outlined" class="text-center pa-3">
                  <div class="text-h4 text-warning">{{ duplicatesData.uniqueQuestionsWithDuplicates }}</div>
                  <div class="text-caption">{{ $t('admin.repeatingQuestions') || 'Қайталанатын сұрақтар' }}</div>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-card variant="outlined" class="text-center pa-3">
                  <div class="text-h4 text-error">{{ duplicatesData.totalDuplicates }}</div>
                  <div class="text-caption">{{ $t('admin.duplicatesCount') || 'Дубликат саны' }}</div>
                </v-card>
              </v-col>
            </v-row>

            <!-- Bulk actions -->
            <v-alert type="warning" variant="tonal" class="mb-4">
              <div class="d-flex align-center justify-space-between flex-wrap gap-2">
                <div>
                  <strong>{{ selectedDuplicates.length }}</strong> {{ $t('admin.testsSelected') || 'тест таңдалды' }}
                </div>
                <div class="d-flex gap-2">
                  <v-btn
                    color="primary"
                    variant="outlined"
                    size="small"
                    @click="selectAllDuplicates"
                  >
                    <v-icon class="mr-1">mdi-select-all</v-icon>
                    {{ $t('admin.selectAll') || 'Барлығын таңдау' }}
                  </v-btn>
                  <v-btn
                    color="error"
                    size="small"
                    @click="deleteSelectedDuplicates"
                    :disabled="selectedDuplicates.length === 0"
                    :loading="deletingDuplicates"
                  >
                    <v-icon class="mr-1">mdi-delete</v-icon>
                    {{ $t('admin.deleteSelected') || 'Таңдалғанды өшіру' }}
                  </v-btn>
                  <v-btn
                    color="error"
                    variant="tonal"
                    size="small"
                    @click="openAutoDeleteDuplicatesDialog"
                    :disabled="duplicatesData.totalDuplicates === 0"
                  >
                    <v-icon class="mr-1">mdi-delete-sweep</v-icon>
                    {{ $t('admin.autoDelete') || 'Автоматты өшіру' }}
                  </v-btn>
                </div>
              </div>
            </v-alert>

            <!-- Duplicate groups list -->
            <v-expansion-panels variant="accordion">
              <v-expansion-panel
                v-for="(group, index) in duplicatesData.groups"
                :key="index"
              >
                <v-expansion-panel-title>
                  <div class="d-flex align-center gap-2 flex-grow-1">
                    <v-chip color="error" size="small">{{ group.count }}x</v-chip>
                    <span class="text-truncate" style="max-width: 600px;">
                      {{ group.question }}
                    </span>
                  </div>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <v-list density="compact">
                    <v-list-item
                      v-for="(test, testIndex) in group.tests"
                      :key="test.id"
                      :class="{ 'bg-success-lighten-5': testIndex === 0 }"
                    >
                      <template #prepend>
                        <v-checkbox
                          v-if="testIndex > 0"
                          v-model="selectedDuplicates"
                          :value="test.id"
                          density="compact"
                          hide-details
                        />
                        <v-chip v-else color="success" size="x-small" class="mr-2">
                          {{ $t('admin.originalShort') || 'Ориг' }}
                        </v-chip>
                      </template>

                      <v-list-item-title class="text-body-2">
                        <strong>ID: {{ test.id }}</strong>
                        <span class="text-caption text-medium-emphasis ml-2">
                          {{ formatTopicLabel(test.topic) || ($t('admin.noTopic') || 'Тақырып жоқ') }}
                        </span>
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-caption">
                        {{ test.questionRu?.substring(0, 80) }}...
                      </v-list-item-subtitle>

                      <template #append>
                        <div class="d-flex align-center gap-1">
                          <v-chip v-if="test.video1" size="x-small" color="info">
                            <v-icon size="x-small">mdi-video</v-icon>
                          </v-chip>
                          <span class="text-caption text-medium-emphasis">
                            {{ formatDate(test.createdAt) }}
                          </span>
                          <v-btn
                            v-if="testIndex > 0"
                            icon="mdi-delete"
                            size="x-small"
                            color="error"
                            variant="text"
                            @click.stop="deleteSingleDuplicate(test.id)"
                          />
                        </div>
                      </template>
                    </v-list-item>
                  </v-list>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="closeDuplicatesDialog">
            {{ $t('common.close') || 'Жабу' }}
          </v-btn>
          <v-btn
            color="warning"
            variant="outlined"
            @click="loadDuplicates"
            :loading="duplicatesLoading"
          >
            <v-icon class="mr-1">mdi-refresh</v-icon>
            {{ $t('common.refresh') || 'Жаңарту' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Auto Delete Duplicates Confirmation Dialog -->
    <v-dialog v-model="autoDeleteDuplicatesDialog" max-width="500px">
      <v-card>
        <v-card-title class="bg-error">
          <v-icon class="mr-2">mdi-delete-sweep</v-icon>
          {{ $t('admin.autoDeleteDuplicates') || 'Дубликаттарды автоматты өшіру' }}
        </v-card-title>

        <v-card-text class="pt-4">
          <v-alert type="warning" variant="tonal" class="mb-4">
            <strong>{{ $t('admin.attention') || 'Назар аударыңыз!' }}</strong><br>
            {{ $t('admin.autoDeleteDuplicatesWarning') || 'Бұл әрекет барлық дубликат тесттерді автоматты түрде өшіреді. Әр қайталанатын сұрақтан тек ең ескісі қалады.' }}
          </v-alert>

          <v-radio-group v-model="keepStrategy" class="mb-4">
            <v-radio value="oldest" :label="$t('admin.keepOldest') || 'Ең ескі тестті қалдыру (ID бойынша)'"></v-radio>
            <v-radio value="newest" :label="$t('admin.keepNewest') || 'Ең жаңа тестті қалдыру (ID бойынша)'"></v-radio>
          </v-radio-group>

          <div class="text-body-2 mb-4">
            <strong>{{ $t('admin.testsToDelete') || 'Өшірілетін тесттер саны:' }}</strong>
            <v-chip color="error" size="small" class="ml-2">
              {{ duplicatesData?.totalDuplicates || 0 }}
            </v-chip>
          </div>

          <v-text-field
            v-model="duplicateDeleteConfirmation"
            :label="$t('admin.deleteConfirmLabel') || 'Растау үшін ӨШІРУ деп жазыңыз'"
            variant="outlined"
            density="compact"
            :error="duplicateDeleteConfirmation && duplicateDeleteConfirmation !== 'ӨШІРУ'"
          />
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="autoDeleteDuplicatesDialog = false">
            {{ $t('common.cancel') || 'Бас тарту' }}
          </v-btn>
          <v-btn
            color="error"
            @click="executeAutoDeleteDuplicates"
            :disabled="duplicateDeleteConfirmation !== 'ӨШІРУ'"
            :loading="deletingDuplicates"
          >
            <v-icon class="mr-1">mdi-delete</v-icon>
            {{ $t('admin.deleteAllConfirm') || 'Барлығын өшіру' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Language Mismatch Dialog -->
    <v-dialog v-model="languageMismatchDialog" max-width="1000px" persistent scrollable>
      <v-card>
        <v-card-title class="bg-info d-flex align-center">
          <v-icon class="mr-2">mdi-translate-off</v-icon>
          {{ $t('admin.languageMismatchTitle') || 'Тіл қателері бар тесттер' }}
          <v-spacer></v-spacer>
          <v-chip v-if="languageMismatchData" color="warning" size="small" class="ml-2">
            {{ languageMismatchData.mismatchedCount }} {{ $t('admin.testsUnit') || 'тест' }}
          </v-chip>
        </v-card-title>

        <v-card-text class="pt-4">
          <!-- Loading state -->
          <div v-if="languageMismatchLoading" class="text-center py-8">
            <v-progress-circular indeterminate color="info" size="64"></v-progress-circular>
            <div class="mt-4 text-h6">{{ $t('admin.searchingLanguageMismatch') || 'Тіл қателерін іздеуде...' }}</div>
          </div>

          <!-- No mismatches -->
          <v-alert
            v-else-if="languageMismatchData && languageMismatchData.mismatchedCount === 0"
            type="success"
            variant="tonal"
          >
            <v-icon class="mr-2">mdi-check-circle</v-icon>
            {{ $t('admin.noLanguageMismatch') || 'Тіл қателері табылмады! Барлық тесттер дұрыс тілде жазылған.' }}
          </v-alert>

          <!-- Mismatches found -->
          <div v-else-if="languageMismatchData && languageMismatchData.issueGroups">
            <!-- Statistics -->
            <v-row class="mb-4">
              <v-col cols="12" md="6">
                <v-card variant="outlined" class="text-center pa-3">
                  <div class="text-h4 text-primary">{{ languageMismatchData.totalTests }}</div>
                  <div class="text-caption">Барлық тесттер</div>
                </v-card>
              </v-col>
              <v-col cols="12" md="6">
                <v-card variant="outlined" class="text-center pa-3">
                  <div class="text-h4 text-warning">{{ languageMismatchData.mismatchedCount }}</div>
                  <div class="text-caption">Тіл қатесі бар тесттер</div>
                </v-card>
              </v-col>
            </v-row>

            <!-- Issue groups -->
            <v-expansion-panels variant="accordion">
              <v-expansion-panel
                v-for="(group, index) in languageMismatchData.issueGroups"
                :key="index"
              >
                <v-expansion-panel-title>
                  <div class="d-flex align-center gap-2 flex-grow-1">
                    <v-chip color="warning" size="small">{{ group.count }}</v-chip>
                    <span>{{ group.issue }}</span>
                  </div>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <v-list density="compact">
                    <v-list-item
                      v-for="test in group.tests"
                      :key="test.id"
                      class="mb-2"
                    >
                      <v-card variant="outlined" class="pa-3 w-100">
                        <div class="d-flex align-center mb-2">
                          <v-chip size="x-small" color="primary" class="mr-2">ID: {{ test.id }}</v-chip>
                          <v-chip v-if="test.topic" size="x-small" variant="outlined">{{ test.topic }}</v-chip>
                        </div>
                        
                        <div class="text-body-2 mb-2">
                          <strong>RU:</strong> {{ test.questionRu?.substring(0, 100) }}...
                        </div>
                        <div class="text-body-2 mb-2">
                          <strong>KZ:</strong> {{ test.questionKz?.substring(0, 100) }}...
                        </div>
                        
                        <div v-if="test.answersRu?.length" class="text-caption text-medium-emphasis mb-1">
                          <strong>RU жауаптар:</strong> {{ test.answersRu.join(' | ') }}
                        </div>
                        <div v-if="test.answersKz?.length" class="text-caption text-medium-emphasis">
                          <strong>KZ жауаптар:</strong> {{ test.answersKz.join(' | ') }}
                        </div>

                        <div class="mt-2">
                          <v-btn
                            size="small"
                            color="primary"
                            variant="outlined"
                            @click="editTest(test)"
                          >
                            <v-icon size="small" class="mr-1">mdi-pencil</v-icon>
                            Өңдеу
                          </v-btn>
                        </div>
                      </v-card>
                    </v-list-item>
                  </v-list>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="closeLanguageMismatchDialog">
            Жабу
          </v-btn>
          <v-btn
            color="info"
            variant="outlined"
            @click="loadLanguageMismatches"
            :loading="languageMismatchLoading"
          >
            <v-icon class="mr-1">mdi-refresh</v-icon>
            Жаңарту
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Expanded Text Dialog -->
    <v-dialog v-model="expandedTextDialog" max-width="800px">
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center bg-primary text-white">
          <span>{{ expandedTextTitle }}</span>
          <v-btn icon="mdi-close" variant="text" color="white" @click="expandedTextDialog = false"></v-btn>
        </v-card-title>
        <v-card-text class="pa-4">
          <v-textarea
            v-model="expandedTextValue"
            variant="outlined"
            rows="10"
            auto-grow
            hide-details
            autofocus
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="expandedTextDialog = false">
            {{ $t('common.cancel') || 'Отмена' }}
          </v-btn>
          <v-btn color="primary" @click="saveExpandedText">
            {{ $t('common.save') || 'Сохранить' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Video Preview Dialog -->
    <v-dialog v-model="videoPreviewDialog" max-width="800px">
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>{{ $t('test.videoPreview') || 'Просмотр видео' }}</span>
          <v-btn icon="mdi-close" variant="text" @click="videoPreviewDialog = false"></v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <video
            v-if="currentVideoUrl"
            :src="currentVideoUrl"
            controls
            autoplay
            style="width: 100%; max-height: 600px;"
          ></video>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<style scoped>
.text-error {
  color: rgb(var(--v-theme-error)) !important;
  font-weight: 500;
}

.text-wrap {
  word-break: break-word;
  white-space: normal;
}

.answer-card {
  position: relative;
}

.answer-card.has-empty-text {
  border: 1px solid rgb(var(--v-theme-error));
  background-color: rgba(var(--v-theme-error), 0.05);
}

.answer-card.has-empty-text .text-body-1 {
  color: rgb(var(--v-theme-error));
}
</style>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSnackbarStore } from '@/stores/snackbar'
import api from '@/utils/api'
import { withBasePath } from '@/utils/path'
import html2pdf from 'html2pdf.js'
import { jsPDF } from 'jspdf'

const { t, locale } = useI18n()
const snackbarStore = useSnackbarStore()

// Reactive data
const tests = ref([])
const backendErrors = ref([]) // Tests with errors from backend
const loading = ref(false)
const saving = ref(false)
const deleting = ref(false)
const searchQuery = ref('')
const selectedTopic = ref('')
const selectedErrorFilter = ref('')
const showValidationErrors = ref(false)

// New topic form
const showNewTopicForm = ref(false)
const newTopic = ref({ ru: '', kz: '', en: '' })

// Topic editor
const topicEditorDialog = ref(false)
const newTopicEditor = ref({ number: null, ru: '', kz: '', en: '' })
const editingTopicIndex = ref(null)
const editingTopic = ref({ number: null, ru: '', kz: '', en: '' })
const originalTopicValue = ref('')
const topicSaving = ref(false)
const deleteTopicDialog = ref(false)
const deletingTopic = ref('')
const topicDeleting = ref(false)

// Dialog states
const testDialog = ref(false)
const closeConfirmDialog = ref(false)
const deleteDialog = ref(false)
const deleteAllDialog = ref(false)
const autoCleanDialog = ref(false)
const editingTest = ref(null)
const testToDelete = ref(null)
const originalTestData = ref(null) // Store original data for comparison

// Auto clean functionality
const autoCleanLoading = ref(false)
const autoCleanConfirmation = ref('')
const autoCleanOptions = ref({
  removeEmptyQuestions: true,
  removeEmptyAnswers: true,
  removeTestsWithoutCorrectAnswer: true
})

// Delete all confirmation
const confirmationText = ref('')
const deletingAll = ref(false)

// Statistics
const statsDialog = ref(false)
const currentTestStats = ref(null)
const statsLoading = ref(false)

// Untaken tests tool
const untakenDialog = ref(false)
const untakenLoading = ref(false)
const untakenTests = ref([])
const untakenHeaders = computed(() => [
  { title: 'ID', key: 'id', width: 70 },
  { title: t('test.topic'), key: 'topic', width: 180 },
  { title: t('test.question'), key: 'question' },
  { title: t('common.status'), key: 'issues', width: 200, sortable: false },
  { title: t('common.actions'), key: 'actions', width: 120, sortable: false }
])

// Map backend error codes (returned by /admin/tests/errors) to readable Kazakh
// labels for tooltips and chips.
const ERROR_LABELS = {
  emptyQuestionRu: 'RU сұрақ жоқ',
  emptyQuestionKz: 'KZ сұрақ жоқ',
  emptyQuestionEn: 'EN сұрақ жоқ',
  sameQuestionRuKz: 'RU/KZ сұрақтар бірдей',
  invalidOptionsJson: 'жауап JSON-ы бұзылған',
  lessThan2Options: 'жауап нұсқалары жетпейді',
  emptyOption: 'бос жауап нұсқасы',
  duplicateOption: 'қайталанған жауап',
  noCorrectAnswer: 'дұрыс жауап белгіленбеген',
  correctAnswerOutOfRange: 'дұрыс жауап индексі қате',
  emptyTopic: 'тақырып жоқ',
  malformedTopic: 'тақырып форматы қате'
}

function describeTestErrors(item) {
  const out = []
  if (Array.isArray(item?.errors)) {
    for (const code of item.errors) {
      out.push(ERROR_LABELS[code] || code)
    }
  }
  // Fallback to legacy local flags (when backend errors aren't on the row)
  if (out.length === 0) {
    if (item?.hasEmptyQuestion) out.push(ERROR_LABELS.emptyQuestionRu)
    if (item?.hasEmptyOptions) out.push(ERROR_LABELS.emptyOption)
  }
  return out
}

// Parse "26|RU|KZ|EN" → readable label using current locale (KZ default for chip).
function formatTopicLabel(raw) {
  if (!raw) return ''
  const parts = String(raw).split('|').map(s => s.trim()).filter(Boolean)
  // Format detected as NUM|RU|KZ|EN. Show "26. жол белгілері".
  if (parts.length >= 3 && /^\d+$/.test(parts[0])) {
    const num = parts[0]
    const kz = parts[2] || parts[1]
    return `${num}. ${kz}`
  }
  return raw
}

async function loadUntakenTests() {
  untakenLoading.value = true
  try {
    const { data } = await api.get('/admin/tests/untaken')
    untakenTests.value = data.tests || []
  } catch (err) {
    snackbarStore.error('Тапсырылмаған тесттерді жүктеу кезінде қате')
  } finally {
    untakenLoading.value = false
  }
}

async function openUntakenDialog() {
  untakenDialog.value = true
  await loadUntakenTests()
}

function editFromUntaken(test) {
  // Keep the Untaken dialog open underneath so the reviewer returns to the
  // list after closing the edit dialog.
  editTest(test)
}

async function deleteFromUntaken(test) {
  if (!confirm(`#${test.id} тестті өшіруді растайсыз ба?`)) return
  try {
    await api.delete(`/admin/tests/${test.id}`)
    untakenTests.value = untakenTests.value.filter(t => t.id !== test.id)
    snackbarStore.success('Тест өшірілді')
  } catch (err) {
    snackbarStore.error('Өшіру кезінде қате')
  }
}

// Video upload
const uploadingVideo = ref(false)
const deletingVideo = ref(null)
const currentVideos = ref({
  video1: null,
  video2: null
})
const noVideo = ref(false)
const animationType = ref('noVideo') // 'noVideo' | 'logoMedical'

// AI Video Generation
const aiEnabled = ref(false)

async function loadAiEnabled() {
  try {
    const res = await api.get('/admin/ai/settings')
    aiEnabled.value = res.data.isEnabled && res.data.hasApiKey
  } catch { aiEnabled.value = false }
}

const aiGeneratePrompt = ref('')
const aiModifyPromptV1 = ref('')
const aiModifyPromptV2 = ref('')
const aiGenerating = ref(false)
const aiModifyingV1 = ref(false)
const aiModifyingV2 = ref(false)
const aiJobStatus = ref(null) // { type, message, jobId, status, videoField }
const aiPolling = ref(false)
const aiApplying = ref(false)

async function startAiGenerate() {
  aiGenerating.value = true
  try {
    const res = await api.post('/admin/ai/generate', {
      prompt: aiGeneratePrompt.value,
      testId: editingTest.value?.id,
      duration: 10
    })
    aiJobStatus.value = {
      type: 'info',
      message: t('aiSettings.generationStarted', { jobId: res.data.jobId }),
      jobId: res.data.jobId,
      status: 'running',
      videoField: 'video1'
    }
    aiGeneratePrompt.value = ''
    setTimeout(() => { if (aiJobStatus.value?.jobId === res.data.jobId) pollAiJob(res.data.jobId) }, 30000)
  } catch (e) {
    aiJobStatus.value = { type: 'error', message: e.response?.data?.error || t('aiSettings.generationError') }
  } finally {
    aiGenerating.value = false
  }
}

async function startAiModifyFor(videoField) {
  const isV1 = videoField === 'video1'
  if (isV1) aiModifyingV1.value = true; else aiModifyingV2.value = true
  try {
    const sourceUrl = currentVideos.value[videoField]
    if (!sourceUrl) {
      aiJobStatus.value = { type: 'warning', message: t('aiSettings.noSourceVideo') }
      return
    }
    const prompt = isV1 ? aiModifyPromptV1.value : aiModifyPromptV2.value
    const res = await api.post('/admin/ai/modify', {
      sourceUrl,
      prompt: prompt || '',
      testId: editingTest.value?.id,
      duration: 10
    })
    aiJobStatus.value = {
      type: 'info',
      message: t('aiSettings.modifyStarted', { video: isV1 ? t('test.video1') : t('test.video2'), jobId: res.data.jobId }),
      jobId: res.data.jobId,
      status: 'running',
      videoField
    }
    if (isV1) aiModifyPromptV1.value = ''; else aiModifyPromptV2.value = ''
    setTimeout(() => { if (aiJobStatus.value?.jobId === res.data.jobId) pollAiJob(res.data.jobId) }, 30000)
  } catch (e) {
    aiJobStatus.value = { type: 'error', message: e.response?.data?.error || t('aiSettings.modifyError') }
  } finally {
    if (isV1) aiModifyingV1.value = false; else aiModifyingV2.value = false
  }
}

async function pollAiJob(jobId) {
  aiPolling.value = true
  try {
    const res = await api.get(`/admin/ai/jobs/${jobId}/status`)
    if (res.data.status === 'completed') {
      aiJobStatus.value = {
        type: 'success',
        message: t('aiSettings.videoReady'),
        jobId,
        status: 'completed'
      }
    } else if (res.data.status === 'failed') {
      aiJobStatus.value = {
        type: 'error',
        message: `${t('common.error')}: ${res.data.errorMsg || t('aiSettings.unknownError')}`,
        jobId,
        status: 'failed'
      }
    } else {
      aiJobStatus.value = {
        type: 'info',
        message: t('aiSettings.inProgress', { progress: res.data.progress ? Math.round(res.data.progress * 100) + '%' : '' }),
        jobId,
        status: 'running'
      }
      // Keep polling
      setTimeout(() => pollAiJob(jobId), 15000)
    }
  } catch (e) {
    aiJobStatus.value = { type: 'error', message: t('aiSettings.statusCheckError') }
  } finally {
    aiPolling.value = false
  }
}

async function applyAiVideo(jobId) {
  aiApplying.value = true
  try {
    const videoField = aiJobStatus.value?.videoField || 'video1'
    const res = await api.post(`/admin/ai/jobs/${jobId}/apply`, {
      testId: editingTest.value?.id,
      videoField
    })
    // Ескі видео сақталады, жаңа нәтиже қосылады
    currentVideos.value[videoField] = res.data.videoPath
    aiJobStatus.value = { type: 'success', message: t('aiSettings.videoApplied', { video: videoField === 'video1' ? t('test.video1') : t('test.video2') }) }
    snackbarStore.success(t('aiSettings.videoAppliedSnack'))
  } catch (e) {
    aiJobStatus.value = { type: 'error', message: e.response?.data?.error || t('aiSettings.applyError') }
  } finally {
    aiApplying.value = false
  }
}

// Video preview
const videoPreviewDialog = ref(false)
const currentVideoUrl = ref('')

// Expanded Text Dialog
const expandedTextDialog = ref(false)
const expandedTextTitle = ref('')
const expandedTextValue = ref('')
const expandedTextTarget = ref(null)

const openExpandedText = (title, value, target) => {
  expandedTextTitle.value = title
  expandedTextValue.value = value
  expandedTextTarget.value = target
  expandedTextDialog.value = true
}

const saveExpandedText = () => {
  if (!expandedTextTarget.value) return

  const { type, lang, index } = expandedTextTarget.value
  
  if (type === 'question') {
    if (lang === 'ru') testData.value.questionRu = expandedTextValue.value
    else if (lang === 'kz') testData.value.questionKz = expandedTextValue.value
    else if (lang === 'en') testData.value.questionEn = expandedTextValue.value
  } else if (type === 'explanation') {
    if (lang === 'ru') testData.value.explanationRu = expandedTextValue.value
    else if (lang === 'kz') testData.value.explanationKz = expandedTextValue.value
    else if (lang === 'en') testData.value.explanationEn = expandedTextValue.value
  } else if (type === 'option' && typeof index === 'number') {
    if (lang === 'ru') testData.value.options[index].textRu = expandedTextValue.value
    else if (lang === 'kz') testData.value.options[index].textKz = expandedTextValue.value
    else if (lang === 'en') testData.value.options[index].textEn = expandedTextValue.value
  }
  
  expandedTextDialog.value = false
}

// Form data
const formValid = ref(false)
const testForm = ref(null)
// Single language selector for all sections
const currentLanguage = ref('ru')

const testData = ref({
  questionRu: '',
  questionKz: '',
  questionEn: '',
  topic: '',
  video1: null,
  video2: null,
  options: [
    { textRu: '', textKz: '', textEn: '', isCorrect: false },
    { textRu: '', textKz: '', textEn: '', isCorrect: false },
    { textRu: '', textKz: '', textEn: '', isCorrect: false },
    { textRu: '', textKz: '', textEn: '', isCorrect: false }
  ],
  explanationRu: '',
  explanationKz: '',
  explanationEn: ''
})

// Error filter options
const errorFilterOptions = computed(() => [
  { title: t('admin.errorFilterAll') || 'Барлық тесттер', value: 'all' },
  { title: t('admin.errorFilterHasErrors') || 'Қатесі бар тесттер', value: 'hasErrors' },
  { title: t('admin.errorFilterNoErrors') || 'Қатесі жоқ тесттер', value: 'noErrors' }
])

// PDF Generator
const pdfGeneratorDialog = ref(false)
const generatingPdf = ref(false)
const pdfConfig = ref({
  topics: [],
  language: 'ru'
})

// Duplicates management
const duplicatesDialog = ref(false)
const duplicatesLoading = ref(false)
const duplicatesData = ref(null)
const selectedDuplicates = ref([])
const deletingDuplicates = ref(false)
const autoDeleteDuplicatesDialog = ref(false)
const keepStrategy = ref('oldest')
const duplicateDeleteConfirmation = ref('')

// Language mismatch management
const languageMismatchDialog = ref(false)
const languageMismatchLoading = ref(false)
const languageMismatchData = ref(null)

const languageOptions = ref([
  { title: 'Русский', value: 'ru' },
  { title: 'Қазақша', value: 'kz' },
  { title: 'English', value: 'en' }
])

// Import state and actions
// Import functionality
const importDialog = ref(false)
const importFile = ref(null)
const importFiles = ref([])
const importLoading = ref(false)
const importProgress = ref({ current: 0, total: 0 })
const importProgressText = ref('')

const openImportDialog = () => { 
  importDialog.value = true
  importFiles.value = []
  importFile.value = null
}

// Template download function
const downloadTemplate = async (format) => {
  try {
    const response = await api.get(`/admin/tests/template/${format}`, {
      responseType: 'blob'
    })
    const blob = new Blob([response.data])
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `test_template.${format}`
    link.click()
    window.URL.revokeObjectURL(url)
    snackbarStore.success(`Шаблон жүктелді: ${format.toUpperCase()}`)
  } catch (error) {
    console.error('Template download error:', error)
    snackbarStore.error('Шаблонды жүктеу кезінде қате')
  }
}

// File helper functions
const getFileTypeIcon = (fileName) => {
  const ext = fileName.split('.').pop().toLowerCase()
  switch (ext) {
    case 'txt': return 'mdi-text-box'
    case 'xlsx': 
    case 'xls': return 'mdi-file-excel'
    case 'json': return 'mdi-code-json'
    default: return 'mdi-file'
  }
}

const getFileTypeColor = (fileName) => {
  const ext = fileName.split('.').pop().toLowerCase()
  switch (ext) {
    case 'txt': return 'blue'
    case 'xlsx':
    case 'xls': return 'green'
    case 'json': return 'orange'
    default: return 'grey'
  }
}

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const removeFile = (index) => {
  importFiles.value.splice(index, 1)
}

const closeImportDialog = () => {
  importDialog.value = false
  importFiles.value = []
  importFile.value = null
  importProgress.value = { current: 0, total: 0 }
  importProgressText.value = ''
}

// Extract category from file path/name
const extractCategoryFromPath = (file) => {
  const path = file.webkitRelativePath || file.name
  
  // Try to extract from path like: Тесты/BAZA_PDD/Вопросы TXT/A/file.txt
  const pathParts = path.split(/[\/\\]/)
  
  // Look for category indicators
  for (let i = pathParts.length - 1; i >= 0; i--) {
    const part = pathParts[i].toUpperCase()
    if (['A', 'B', 'C', 'D', 'E'].includes(part)) {
      return part
    }
  }
  
  // Fallback to filename analysis
  const fileName = file.name.toUpperCase()
  if (fileName.includes('_A_') || fileName.startsWith('A_')) return 'A'
  if (fileName.includes('_B_') || fileName.startsWith('B_')) return 'B'
  if (fileName.includes('_C_') || fileName.startsWith('C_')) return 'C'
  if (fileName.includes('_D_') || fileName.startsWith('D_')) return 'D'
  if (fileName.includes('_E_') || fileName.startsWith('E_')) return 'E'
  
  return 'GENERAL'
}

// Single file import (existing functionality)
const doImport = async () => {
  try {
    const file = Array.isArray(importFile.value) ? importFile.value[0] : importFile.value
    if (!file) return
    importLoading.value = true
    const form = new FormData()
    form.append('file', file)
  // Не задаем Content-Type вручную, браузер проставит boundary сам
  const { data } = await api.post('/admin/tests/import', form)
  snackbarStore.success(`Импорт аяқталды: ${data.created} тест жасалды`)
    importDialog.value = false
    importFile.value = null
    await loadTests()
  } catch (e) {
    // Error
    const msg = e.response?.data?.error || 'Импорт кезінде қате орын алды'
    snackbarStore.error(msg)
  } finally {
    importLoading.value = false
  }
}

// Multiple files import
const doMultipleImport = async () => {
  if (!importFiles.value || importFiles.value.length === 0) return
  
  try {
    importLoading.value = true
    importProgress.value = { current: 0, total: importFiles.value.length }
    
    let totalCreated = 0
    let totalErrors = 0
    
    for (let i = 0; i < importFiles.value.length; i++) {
      const file = importFiles.value[i]
      importProgress.value.current = i + 1
      importProgressText.value = `Обработка: ${file.name}`
      
      try {
        const form = new FormData()
        form.append('file', file)
        
        // Don't send category - backend extracts topic from filename
        
        const { data } = await api.post('/admin/tests/import', form)
        totalCreated += data.created || 0
        
        // Small delay to show progress
        await new Promise(resolve => setTimeout(resolve, 200))
        
      } catch (fileError) {
        // Error
        totalErrors++
      }
    }
    
    // Show results
    if (totalErrors === 0) {
      snackbarStore.success(`Импорт аяқталды: ${totalCreated} тест жасалды`)
    } else {
      snackbarStore.warning(`Импорт аяқталды: ${totalCreated} тест жасалды, ${totalErrors} қате орын алды`)
    }
    
    closeImportDialog()
    await loadTests()
    
  } catch (e) {
    // Error
    snackbarStore.error('Импорт кезінде жалпы қате')
  } finally {
    importLoading.value = false
  }
}

// Computed
const uniqueTopics = computed(() => {
  // Get topics from tests
  const testTopics = tests.value
    .map(test => test.topic)
    .filter(topic => topic && topic.trim() !== '')
  
  // Get custom topics from localStorage
  const customTopics = JSON.parse(localStorage.getItem('customTopics') || '[]')
  
  // Combine and deduplicate
  const allTopics = [...new Set([...testTopics, ...customTopics])]
  return allTopics.sort()
})

// Sorted topics by number
const sortedTopics = computed(() => {
  return [...uniqueTopics.value].sort((a, b) => {
    const numA = getTopicNumber(a) || 999
    const numB = getTopicNumber(b) || 999
    return numA - numB
  })
})

// Get used topic numbers
const usedTopicNumbers = computed(() => {
  const numbers = new Set()
  uniqueTopics.value.forEach(topic => {
    const num = getTopicNumber(topic)
    if (num) numbers.add(num)
  })
  return numbers
})

// Available topic numbers (1-99, excluding used ones)
const availableTopicNumbers = computed(() => {
  const available = []
  for (let i = 1; i <= 99; i++) {
    if (!usedTopicNumbers.value.has(i)) {
      available.push(i)
    }
  }
  return available
})

// Topic items for combobox with multilingual display
const topicItems = computed(() => {
  return sortedTopics.value.map(topic => ({
    title: formatTopicDisplay(topic),
    value: topic
  }))
})

// Parse topic number from string (format: "NN|RU|KZ|EN" or "NN|Text")
const getTopicNumber = (topic) => {
  if (!topic) return null
  const parts = topic.split('|')
  const firstPart = parts[0]?.trim()
  // Check if first part is a number (1-2 digits)
  if (/^\d{1,2}$/.test(firstPart)) {
    return parseInt(firstPart, 10)
  }
  return null
}

// Get topic text without number
const getTopicText = (topic, locale = 'ru') => {
  if (!topic) return ''
  const parts = topic.split('|')
  const hasNumber = /^\d{1,2}$/.test(parts[0]?.trim())
  
  if (hasNumber) {
    // Format: "NN|RU|KZ|EN" or "NN|Text"
    if (parts.length >= 4) {
      const localeIndex = { ru: 1, kz: 2, en: 3 }
      return parts[localeIndex[locale]] || parts[1] || ''
    } else if (parts.length === 2) {
      return parts[1] || ''
    }
  } else {
    // Format: "RU|KZ|EN" or "Text"
    if (parts.length >= 3) {
      const localeIndex = { ru: 0, kz: 1, en: 2 }
      return parts[localeIndex[locale]] || parts[0] || topic
    }
  }
  return topic
}

// Check if topic has multiple languages
const hasMultipleLanguages = (topic) => {
  if (!topic) return false
  const parts = topic.split('|')
  const hasNumber = /^\d{1,2}$/.test(parts[0]?.trim())
  return hasNumber ? parts.length >= 4 : parts.length >= 3
}

// Format topic for display (with number)
const formatTopicDisplay = (topic) => {
  const num = getTopicNumber(topic)
  const text = getTopicText(topic, locale.value)
  if (num) {
    return `${num}. ${text}`
  }
  return text || topic
}

// Get available numbers for editing (current number + free numbers)
const getAvailableNumbersForEdit = (topic) => {
  const currentNum = getTopicNumber(topic)
  const available = [...availableTopicNumbers.value]
  if (currentNum && !available.includes(currentNum)) {
    available.push(currentNum)
    available.sort((a, b) => a - b)
  }
  return available
}

// New topic functions
const cancelNewTopic = () => {
  showNewTopicForm.value = false
  newTopic.value = { ru: '', kz: '', en: '' }
}

const applyNewTopic = () => {
  // Create topic string - use the first non-empty value as the main topic
  // Format: "NN|RU_TEXT|KZ_TEXT|EN_TEXT" for multilingual or "NN|text" for single
  const ru = newTopic.value.ru?.trim() || ''
  const kz = newTopic.value.kz?.trim() || ''
  const en = newTopic.value.en?.trim() || ''
  
  // If all three are filled, create multilingual format
  if (ru && kz && en) {
    testData.value.topic = `${ru}|${kz}|${en}`
  } else {
    // Use the first non-empty value
    testData.value.topic = ru || kz || en
  }
  
  showNewTopicForm.value = false
  newTopic.value = { ru: '', kz: '', en: '' }
}

// Get localized topic display (compatibility function)
const getLocalizedTopic = (topic, loc = 'ru') => {
  return getTopicText(topic, loc)
}

// Topic Editor Functions
const openTopicEditorDialog = () => {
  topicEditorDialog.value = true
  newTopicEditor.value = { ru: '', kz: '', en: '' }
  editingTopicIndex.value = null
}

const getTestCountByTopic = (topic) => {
  return tests.value.filter(t => t.topic === topic).length
}

const addTopicInEditor = async () => {
  const num = newTopicEditor.value.number
  const ru = newTopicEditor.value.ru?.trim() || ''
  const kz = newTopicEditor.value.kz?.trim() || ''
  const en = newTopicEditor.value.en?.trim() || ''
  
  if ((!ru && !kz && !en) || !num) return
  
  // Create topic string with number: "NN|RU|KZ|EN" or "NN|Text"
  let topicValue
  if (ru && kz && en) {
    topicValue = `${num}|${ru}|${kz}|${en}`
  } else {
    topicValue = `${num}|${ru || kz || en}`
  }
  
  // Check if topic number already exists
  if (usedTopicNumbers.value.has(num)) {
    snackbarStore.error('Бұл нөмір бұрыннан бар')
    return
  }
  
  // Save to localStorage custom topics
  const customTopics = JSON.parse(localStorage.getItem('customTopics') || '[]')
  if (!customTopics.includes(topicValue)) {
    customTopics.push(topicValue)
    localStorage.setItem('customTopics', JSON.stringify(customTopics))
  }
  
  snackbarStore.success('Тақырып қосылды')
  newTopicEditor.value = { number: null, ru: '', kz: '', en: '' }
  
  // Refresh tests to update uniqueTopics
  await loadTests()
}

const startEditTopic = (index, topic) => {
  editingTopicIndex.value = index
  originalTopicValue.value = topic
  
  // Parse topic with number
  const num = getTopicNumber(topic)
  const parts = topic.split('|')
  const hasNumber = /^\d{1,2}$/.test(parts[0]?.trim())
  
  if (hasNumber && parts.length >= 4) {
    // Format: "NN|RU|KZ|EN"
    editingTopic.value = {
      number: num,
      ru: parts[1] || '',
      kz: parts[2] || '',
      en: parts[3] || ''
    }
  } else if (hasNumber && parts.length === 2) {
    // Format: "NN|Text"
    editingTopic.value = {
      number: num,
      ru: parts[1] || '',
      kz: parts[1] || '',
      en: parts[1] || ''
    }
  } else if (parts.length >= 3) {
    // Format: "RU|KZ|EN" (old format without number)
    editingTopic.value = {
      number: null,
      ru: parts[0] || '',
      kz: parts[1] || '',
      en: parts[2] || ''
    }
  } else {
    editingTopic.value = {
      number: null,
      ru: topic,
      kz: topic,
      en: topic
    }
  }
}

const cancelEditTopic = () => {
  editingTopicIndex.value = null
  editingTopic.value = { number: null, ru: '', kz: '', en: '' }
  originalTopicValue.value = ''
}

const saveEditTopic = async () => {
  const num = editingTopic.value.number
  const ru = editingTopic.value.ru?.trim() || ''
  const kz = editingTopic.value.kz?.trim() || ''
  const en = editingTopic.value.en?.trim() || ''
  
  if (!ru && !kz && !en) {
    snackbarStore.error('Кем дегенде бір тіл толтырылуы керек')
    return
  }
  
  // Create new topic value with number
  let newTopicValue
  if (num && ru && kz && en) {
    // Format: "NN|RU|KZ|EN" with number
    newTopicValue = `${num}|${ru}|${kz}|${en}`
  } else if (num) {
    // Has number but not all languages
    const text = ru || kz || en
    newTopicValue = `${num}|${text}|${text}|${text}`
  } else if (ru && kz && en) {
    // No number but all languages (old format)
    newTopicValue = `${ru}|${kz}|${en}`
  } else {
    // Simple topic (single text)
    newTopicValue = ru || kz || en
  }
  
  if (newTopicValue === originalTopicValue.value) {
    cancelEditTopic()
    return
  }
  
  topicSaving.value = true
  
  try {
    // Update all tests with the old topic to the new topic
    const testsToUpdate = tests.value.filter(t => t.topic === originalTopicValue.value)
    
    for (const test of testsToUpdate) {
      await api.put(`/admin/tests/${test.id}`, {
        ...test,
        topic: newTopicValue
      })
    }
    
    // Update custom topics in localStorage
    const customTopics = JSON.parse(localStorage.getItem('customTopics') || '[]')
    const oldIndex = customTopics.indexOf(originalTopicValue.value)
    if (oldIndex !== -1) {
      customTopics[oldIndex] = newTopicValue
      localStorage.setItem('customTopics', JSON.stringify(customTopics))
    }
    
    snackbarStore.success(`${testsToUpdate.length} тест жаңартылды`)
    cancelEditTopic()
    await loadTests()
  } catch (error) {
    console.error('Error updating topic:', error)
    snackbarStore.error('Тақырыпты жаңарту қатесі')
  } finally {
    topicSaving.value = false
  }
}

const confirmDeleteTopic = (topic) => {
  deletingTopic.value = topic
  deleteTopicDialog.value = true
}

const executeDeleteTopic = async () => {
  topicDeleting.value = true
  
  try {
    // Clear topic from all tests with this topic
    const testsToUpdate = tests.value.filter(t => t.topic === deletingTopic.value)
    
    for (const test of testsToUpdate) {
      await api.put(`/admin/tests/${test.id}`, {
        ...test,
        topic: ''
      })
    }
    
    // Remove from custom topics in localStorage
    const customTopics = JSON.parse(localStorage.getItem('customTopics') || '[]')
    const index = customTopics.indexOf(deletingTopic.value)
    if (index !== -1) {
      customTopics.splice(index, 1)
      localStorage.setItem('customTopics', JSON.stringify(customTopics))
    }
    
    snackbarStore.success(`Тақырып жойылды. ${testsToUpdate.length} тесттен тазаланды.`)
    deleteTopicDialog.value = false
    deletingTopic.value = ''
    await loadTests()
  } catch (error) {
    console.error('Error deleting topic:', error)
    snackbarStore.error('Тақырыпты жою қатесі')
  } finally {
    topicDeleting.value = false
  }
}

const testsWithErrors = computed(() => {
  // Use backend validated error tests
  // Log removed;
  // Log removed);
  return backendErrors.value
})

const filteredTests = computed(() => {
  let filtered = tests.value
  
  // Log removed;
  // Log removed;

  // Apply error filter FIRST
  if (selectedErrorFilter.value && selectedErrorFilter.value === 'hasErrors') {
    filtered = testsWithErrors.value
    // Log removed;
    // Log removed.map(t => t.id));
  } else if (selectedErrorFilter.value === 'noErrors') {
    // Show only tests without errors
    const errorIds = new Set(testsWithErrors.value.map(t => t.id))
    filtered = filtered.filter(test => !errorIds.has(test.id))
    // Log removed;
  }

  if (searchQuery.value) {
    filtered = filtered.filter(test => 
      test.questionRu?.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      test.questionKz?.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      test.questionEn?.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      test.video1?.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      test.video2?.toLowerCase().includes(searchQuery.value.toLowerCase())
    )
    // Log removed;
  }

  if (selectedTopic.value) {
    filtered = filtered.filter(test => test.topic === selectedTopic.value)
    // Log removed;
  }

  return filtered.map(test => {
    let parsedOptions = []
    try {
      if (test.answersRu) {
        parsedOptions = JSON.parse(test.answersRu)
      } else if (test.options) {
        parsedOptions = typeof test.options === 'string' ? JSON.parse(test.options) : test.options
      }
    } catch (error) {
      // Error
      parsedOptions = []
    }

    return {
      ...test,
      question: test.questionRu || test.question,
      options: parsedOptions,
      explanation: test.explanationRu || test.explanation,
      // Add validation flags for empty values
      hasEmptyQuestion: isEmptyOrWhitespace(test.questionRu || test.question),
      hasEmptyOptions: parsedOptions.some(option => {
        if (typeof option === 'string') {
          return isEmptyOrWhitespace(option)
        } else if (option && typeof option === 'object') {
          const optionText = option.text || option.answer || option.textRu || String(option)
          return isEmptyOrWhitespace(optionText)
        }
        return true
      }),
      emptyOptionsCount: parsedOptions.filter(option => {
        if (typeof option === 'string') {
          return isEmptyOrWhitespace(option)
        } else if (option && typeof option === 'object') {
          const optionText = option.text || option.answer || option.textRu || String(option)
          return isEmptyOrWhitespace(optionText)
        }
        return true
      }).length
    }
  })
})

// Validation computed
const hasCorrectAnswer = computed(() => {
  return testData.value.options.some(option => option.isCorrect)
})

const allOptionsHaveText = computed(() => {
  return testData.value.options.every(option => 
    (option.textRu && option.textRu.trim()) &&
    (option.textKz && option.textKz.trim()) &&
    (option.textEn && option.textEn.trim())
  )
})

const currentTestErrors = computed(() => {
  const errors = []
  
  // Check questions
  if (!testData.value.questionRu || !testData.value.questionRu.trim()) {
    errors.push('Русский вопрос пуст')
  }
  if (!testData.value.questionKz || !testData.value.questionKz.trim()) {
    errors.push('Қазақша сұрақ бос')
  }
  
  // Check options
  testData.value.options.forEach((option, index) => {
    if (!option.textRu || !option.textRu.trim()) {
      errors.push(`Вариант ${index + 1} (RU) пуст`)
    }
    if (!option.textKz || !option.textKz.trim()) {
      errors.push(`Нұсқа ${index + 1} (KZ) бос`)
    }
  })
  
  // Check correct answer
  if (!hasCorrectAnswer.value) {
    errors.push('Не выбран правильный ответ')
  }
  
  return errors
})

const hasValidationErrors = computed(() => {
  return currentTestErrors.value.length > 0
})

const hasEmptyOptions = computed(() => {
  return testData.value.options.some(option => 
    !option.textRu || !option.textRu.trim() ||
    !option.textKz || !option.textKz.trim() ||
    !option.textEn || !option.textEn.trim()
  )
})

// PDF Generator computed properties
const pdfTestsCount = computed(() => {
  if (!pdfConfig.value.topics || pdfConfig.value.topics.length === 0) return 0
  
  return tests.value.filter(test => 
    pdfConfig.value.topics.includes(test.topic)
  ).length
})

const isFormValid = computed(() => {
  return formValid.value && 
         hasCorrectAnswer.value && 
         allOptionsHaveText.value && 
         testData.value.questionRu && testData.value.questionRu.trim() &&
         testData.value.questionKz && testData.value.questionKz.trim() &&
         testData.value.questionEn && testData.value.questionEn.trim()
})

// Table headers
const headers = computed(() => [
  { title: '№', key: 'number', sortable: false, width: '80px' },
  { title: t('test.question') || 'Сұрақ', key: 'question', sortable: true, width: '50%' },
  { title: t('test.topic') || 'Тақырып', key: 'topic', sortable: true, width: '20%' },
  { title: t('common.actions') || 'Әрекеттер', key: 'actions', sortable: false, width: '200px' }
])

// Methods
const isEmptyOrWhitespace = (text) => {
  return !text || text.trim() === ''
}

const getQuestionByLocale = (item) => {
  const currentLang = locale.value || 'kz'
  
  if (currentLang === 'kz') {
    return item.questionKz || item.questionRu || item.question
  } else if (currentLang === 'ru') {
    return item.questionRu || item.question || item.questionKz
  } else if (currentLang === 'en') {
    return item.questionEn || item.questionRu || item.question
  }
  
  return item.questionRu || item.question || item.questionKz || item.questionEn
}

const loadTests = async () => {
  loading.value = true
  try {
    // Load all tests and tests with errors in parallel
    // Log removed;
    
    const [testsResponse, errorsResponse] = await Promise.all([
      (async () => {
        try {
          // Log removed;
          return await api.get('/admin/tests?all=true')
        } catch (e) {
          // Log removed;
          try {
            return await api.get('/admin/tests?limit=10000')
          } catch (e2) {
            // Log removed;
            return await api.get('/admin/tests')
          }
        }
      })(),
      (async () => {
        try {
          // Log removed;
          const response = await api.get('/admin/tests/errors');
          // Log removed;
          return response;
        } catch (e) {
          // Error;
          return { data: { tests: [] } };
        }
      })()
    ]);
    
    tests.value = testsResponse.data?.tests || []
    backendErrors.value = errorsResponse.data?.tests || []
    
    // Log removed
    // Log removed
    
    if (backendErrors.value.length > 0) {
      // Log removed;
    } else {
      // Warning;
    }
    
    // Debug: Check test structure for error filtering
    if (tests.value.length > 0) {
      // Log removed
      // Log removed
    }
    
    // If still limited, try to get more data
    if (tests.value.length === 20) {
      // Warning
    }
  } catch (error) {
    // Error
    snackbarStore.error('Тесттерді жүктеу кезінде қате орын алды')
  } finally {
    loading.value = false
  }
}

const openCreateDialog = () => {
  editingTest.value = null
  resetTestData()
  noVideo.value = false
  animationType.value = 'noVideo'
  showValidationErrors.value = false
  originalTestData.value = null // No original data for new test
  testDialog.value = true
}

const editTest = (test) => {
  editingTest.value = test
  showValidationErrors.value = false

  // Store current video information
  currentVideos.value = {
    video1: test.video1,
    video2: test.video2
  }
  // Detect "no video" mode from stored video1 (empty/null or sentinel = animation)
  const isNoVideoSentinel = !test.video1 || test.video1 === '__NO_VIDEO__' || test.video1 === '__LOGO_MEDICAL__'
  noVideo.value = isNoVideoSentinel
  animationType.value = test.video1 === '__LOGO_MEDICAL__' ? 'logoMedical' : 'noVideo'
  
  let parsedOptionsRu = []
  let parsedOptionsKz = []
  let parsedOptionsEn = []
  
  try {
    // Parse RU options
    if (test.answersRu) {
      parsedOptionsRu = typeof test.answersRu === 'string' ? JSON.parse(test.answersRu) : test.answersRu
    } else if (test.options) {
      parsedOptionsRu = typeof test.options === 'string' ? JSON.parse(test.options) : test.options
    }
    
    // Parse KZ options
    if (test.answersKz) {
      parsedOptionsKz = typeof test.answersKz === 'string' ? JSON.parse(test.answersKz) : test.answersKz
    }
    
    // Parse EN options
    if (test.answersEn) {
      parsedOptionsEn = typeof test.answersEn === 'string' ? JSON.parse(test.answersEn) : test.answersEn
    }
    
    // Ensure all are arrays
    if (!Array.isArray(parsedOptionsRu)) parsedOptionsRu = []
    if (!Array.isArray(parsedOptionsKz)) parsedOptionsKz = []
    if (!Array.isArray(parsedOptionsEn)) parsedOptionsEn = []
    
  } catch (error) {
    // Error
    parsedOptionsRu = []
    parsedOptionsKz = []
    parsedOptionsEn = []
  }
  
  // Convert options to the multilingual format expected by the form
  const maxLength = Math.max(parsedOptionsRu.length, parsedOptionsKz.length, parsedOptionsEn.length)
  const formattedOptions = []
  
  for (let i = 0; i < maxLength; i++) {
    const optionRu = parsedOptionsRu[i]
    const optionKz = parsedOptionsKz[i]
    const optionEn = parsedOptionsEn[i]
    
    formattedOptions.push({
      textRu: typeof optionRu === 'string' ? optionRu : (optionRu?.textRu || optionRu?.text || String(optionRu || '')),
      textKz: typeof optionKz === 'string' ? optionKz : (optionKz?.textKz || optionKz?.text || String(optionKz || '')),
      textEn: typeof optionEn === 'string' ? optionEn : (optionEn?.textEn || optionEn?.text || String(optionEn || '')),
      isCorrect: test.correctAnswer === i
    })
  }
  
  // ONLY add empty options if we have less than 2 (minimum required)
  while (formattedOptions.length < 2) {
    formattedOptions.push({ textRu: '', textKz: '', textEn: '', isCorrect: false })
  }
  
  // Populate form with multilingual data
  testData.value = {
    questionRu: test.questionRu || test.question || '',
    questionKz: test.questionKz || test.question || '',
    questionEn: test.questionEn || test.question || '',
    topic: test.topic || '',
    video1: null, // Reset video inputs for editing
    video2: null,
    options: formattedOptions,
    explanationRu: test.explanationRu || test.explanation || '',
    explanationKz: test.explanationKz || test.explanation || '',
    explanationEn: test.explanationEn || test.explanation || ''
  }
  
  // Store original data for comparison (deep copy)
  originalTestData.value = {
    questionRu: testData.value.questionRu,
    questionKz: testData.value.questionKz,
    questionEn: testData.value.questionEn,
    topic: testData.value.topic,
    options: JSON.parse(JSON.stringify(testData.value.options)),
    explanationRu: testData.value.explanationRu,
    explanationKz: testData.value.explanationKz,
    explanationEn: testData.value.explanationEn
  }
  
  testDialog.value = true
}

const closeTestDialog = () => {
  testDialog.value = false
  editingTest.value = null
  showValidationErrors.value = false
  currentVideos.value = {
    video1: null,
    video2: null
  }
  noVideo.value = false
  animationType.value = 'noVideo'
  resetTestData()
  originalTestData.value = null
}

// Check if form data has changed from original
const hasFormChanges = () => {
  // For new test - check if any data entered
  if (!originalTestData.value) {
    const hasData = testData.value.questionRu || testData.value.questionKz || testData.value.questionEn ||
      testData.value.topic ||
      testData.value.options.some(opt => opt.textRu || opt.textKz || opt.textEn) ||
      testData.value.explanationRu || testData.value.explanationKz || testData.value.explanationEn
    return hasData
  }
  
  // For editing - compare with original
  const orig = originalTestData.value
  const curr = testData.value
  
  // Compare questions
  if (curr.questionRu !== orig.questionRu ||
      curr.questionKz !== orig.questionKz ||
      curr.questionEn !== orig.questionEn) return true
  
  // Compare topic
  if (curr.topic !== orig.topic) return true
  
  // Compare explanations
  if (curr.explanationRu !== orig.explanationRu ||
      curr.explanationKz !== orig.explanationKz ||
      curr.explanationEn !== orig.explanationEn) return true
  
  // Compare options
  if (curr.options.length !== orig.options.length) return true
  
  for (let i = 0; i < curr.options.length; i++) {
    const currOpt = curr.options[i]
    const origOpt = orig.options[i]
    if (currOpt.textRu !== origOpt.textRu ||
        currOpt.textKz !== origOpt.textKz ||
        currOpt.textEn !== origOpt.textEn ||
        currOpt.isCorrect !== origOpt.isCorrect) return true
  }
  
  return false
}

// Confirm close dialog - ask user to save or discard
const confirmCloseTestDialog = () => {
  // Check if there are any changes
  if (hasFormChanges()) {
    closeConfirmDialog.value = true
  } else {
    closeTestDialog()
  }
}

// Close without saving
const closeWithoutSaving = () => {
  closeConfirmDialog.value = false
  closeTestDialog()
}

// Save and close
const saveAndClose = async () => {
  closeConfirmDialog.value = false
  await saveTest()
}

const resetTestData = () => {
  testData.value = {
    questionRu: '',
    questionKz: '',
    questionEn: '',
    topic: '',
    video1: null,
    video2: null,
    options: [
      { textRu: '', textKz: '', textEn: '', isCorrect: false },
      { textRu: '', textKz: '', textEn: '', isCorrect: false },
      { textRu: '', textKz: '', textEn: '', isCorrect: false },
      { textRu: '', textKz: '', textEn: '', isCorrect: false }
    ],
    explanationRu: '',
    explanationKz: '',
    explanationEn: ''
  }
  
  // Reset language selector
  currentLanguage.value = 'ru'
  
  if (testForm.value) {
    testForm.value.resetValidation()
  }
}

const addOption = () => {
  if (testData.value.options.length < 6) {
    testData.value.options.push({ textRu: '', textKz: '', textEn: '', isCorrect: false })
  }
}

const removeOption = (index) => {
  if (testData.value.options.length > 2) {
    testData.value.options.splice(index, 1)
  }
}

const removeEmptyOptions = () => {
  // Keep only options that have text in all languages
  const nonEmptyOptions = testData.value.options.filter(option => 
    option.textRu && option.textRu.trim() &&
    option.textKz && option.textKz.trim() &&
    option.textEn && option.textEn.trim()
  )
  
  if (nonEmptyOptions.length >= 2) {
    testData.value.options = nonEmptyOptions
  } else {
    // If less than 2 non-empty options, keep non-empty ones and add empty ones to reach 2
    const emptyOptionsToAdd = 2 - nonEmptyOptions.length
    testData.value.options = [
      ...nonEmptyOptions,
      ...Array(emptyOptionsToAdd).fill().map(() => ({ textRu: '', textKz: '', textEn: '', isCorrect: false }))
    ]
  }
  
  snackbarStore.success('Бос нұсқалар өшірілді')
}

const clearAllOptions = () => {
  // Reset to 2 empty options
  testData.value.options = [
    { textRu: '', textKz: '', textEn: '', isCorrect: false },
    { textRu: '', textKz: '', textEn: '', isCorrect: false }
  ]
  snackbarStore.success('Барлық нұсқалар тазаланды')
}

const handleCorrectAnswerChange = (index) => {
  // Ensure only one correct answer
  testData.value.options.forEach((option, i) => {
    if (i !== index) {
      option.isCorrect = false
    }
  })
}

const saveTest = async () => {
  // Show validation errors
  showValidationErrors.value = true
  
  if (!formValid.value) {
    snackbarStore.error('Міндетті өрістерді толтырыңыз')
    return
  }

  // Validate that all options have text
  if (!allOptionsHaveText.value) {
    snackbarStore.error('Барлық жауап нұсқаларын толтырыңыз')
    return
  }

  // Validate that at least one answer is correct
  if (!hasCorrectAnswer.value) {
    snackbarStore.error('Дұрыс жауапты таңдаңыз')
    return
  }

  // Check for duplicate questions (only for new tests)
  // Modified: Allow same questions with different videos
  if (!editingTest.value) {
    const duplicate = tests.value.find(test => {
      // Check if questions match
      const sameQuestion = (test.questionRu === testData.value.questionRu || 
                            test.questionKz === testData.value.questionKz || 
                            test.questionEn === testData.value.questionEn)
      
      // If questions are different, not a duplicate
      if (!sameQuestion) return false
      
      // Questions are same - check if videos are also same
      // Only treat as duplicate if BOTH question AND video are identical
      const video1Same = test.video1 === (testData.value.video1?.name || '')
      
      return sameQuestion && video1Same
    })
    
    if (duplicate) {
      snackbarStore.error('Бұл сұрақ пен видео қазірдің өзінде бар')
      return
    }
  }

  saving.value = true
  uploadingVideo.value = true
  
  try {
    // Create FormData for file upload
    const formData = new FormData()
    
    // Add multilingual test data
    formData.append('questionRu', testData.value.questionRu)
    formData.append('questionKz', testData.value.questionKz)
    formData.append('questionEn', testData.value.questionEn)
    
    // Handle topic - can be string or object from combobox
    const topicValue = typeof testData.value.topic === 'object' 
      ? testData.value.topic?.value || testData.value.topic?.title || ''
      : testData.value.topic || ''
    formData.append('topic', topicValue)
    
    // Convert options to proper format for backend
    const validOptions = testData.value.options.filter(option => 
      option.textRu.trim() && option.textKz.trim() && option.textEn.trim()
    )
    formData.append('answersRu', JSON.stringify(validOptions.map(opt => opt.textRu)))
    formData.append('answersKz', JSON.stringify(validOptions.map(opt => opt.textKz)))
    formData.append('answersEn', JSON.stringify(validOptions.map(opt => opt.textEn)))
    
    // Find correct answer index
    const correctIndex = validOptions.findIndex(opt => opt.isCorrect)
    formData.append('correctAnswer', correctIndex.toString())
    
    formData.append('explanationRu', testData.value.explanationRu || '')
    formData.append('explanationKz', testData.value.explanationKz || '')
    formData.append('explanationEn', testData.value.explanationEn || '')
    
    // Add videos if selected
    // Handle both File object and Array from v-file-input
    const video1File = !noVideo.value && (testData.value.video1 instanceof File
      ? testData.value.video1
      : (testData.value.video1?.[0] || null))
    const video2File = !noVideo.value && (testData.value.video2 instanceof File
      ? testData.value.video2
      : (testData.value.video2?.[0] || null))

    console.log('Video1 file:', video1File)
    console.log('Video2 file:', video2File)

    if (video1File) {
      formData.append('video1', video1File)
    }
    if (video2File) {
      formData.append('video2', video2File)
    }
    if (noVideo.value) {
      formData.append('noVideo', '1')
      formData.append('animationType', animationType.value || 'noVideo')
    }

    let response
    if (editingTest.value) {
      response = await api.put(`/admin/tests/${editingTest.value.id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      snackbarStore.success('Тест жаңартылды')
    } else {
      response = await api.post('/admin/tests', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      snackbarStore.success('Тест жасалды')
    }

    closeTestDialog()
    await loadTests()
    // If the Untaken dialog is open underneath, refresh its rows so the
    // diagnostic chips (empty topic, no video) reflect the new state.
    if (untakenDialog.value) {
      await loadUntakenTests()
    }
  } catch (error) {
    // Error
    snackbarStore.error('Тестті сақтауда қате орын алды')
  } finally {
    saving.value = false
    uploadingVideo.value = false
  }
}

const deleteTest = (test) => {
  testToDelete.value = test
  deleteDialog.value = true
}

const confirmDelete = async () => {
  if (!testToDelete.value) return

  deleting.value = true
  try {
    await api.delete(`/admin/tests/${testToDelete.value.id}`)
  snackbarStore.success('Тест өшірілді')
    await loadTests()
    deleteDialog.value = false
    testToDelete.value = null
  } catch (error) {
    // Error
  snackbarStore.error('Тестті өшіруде қате орын алды')
  } finally {
    deleting.value = false
  }
}

// Delete All Tests Functions
const openDeleteAllDialog = () => {
  confirmationText.value = ''
  deleteAllDialog.value = true
}

const closeDeleteAllDialog = () => {
  deleteAllDialog.value = false
  confirmationText.value = ''
}

// Auto Clean Functions
const openAutoCleanDialog = () => {
  autoCleanConfirmation.value = ''
  autoCleanDialog.value = true
}

const closeAutoCleanDialog = () => {
  autoCleanDialog.value = false
  autoCleanConfirmation.value = ''
}

const getErrorDescription = (test) => {
  const errors = []
  
  // Check questions
  if (!test.questionRu || isEmptyOrWhitespace(test.questionRu)) errors.push('RU сұрақ жоқ')
  if (!test.questionKz || isEmptyOrWhitespace(test.questionKz)) errors.push('KZ сұрақ жоқ')
  if (!test.questionEn || isEmptyOrWhitespace(test.questionEn)) errors.push('EN сұрақ жоқ')
  
  // Check options
  let parsedOptions = []
  try {
    parsedOptions = typeof test.options === 'string' ? JSON.parse(test.options) : (test.options || [])
  } catch (e) {
    errors.push('JSON қатесі')
    return errors.join(', ')
  }
  
  if (parsedOptions.length < 2) errors.push('Аз жауап нұсқалары')
  
  const emptyOptions = parsedOptions.filter(option => {
    if (typeof option === 'string') return !option || isEmptyOrWhitespace(option)
    if (option && typeof option === 'object') {
      return !option.textRu || !option.textKz || !option.textEn
    }
    return true
  })
  
  if (emptyOptions.length > 0) errors.push(`${emptyOptions.length} бос нұсқа`)
  
  // Check correct answer
  const hasCorrectAnswer = parsedOptions.some(option => option?.isCorrect) || 
                          (test.correctAnswer !== undefined && test.correctAnswer >= 0)
  if (!hasCorrectAnswer) errors.push('Дұрыс жауап жоқ')
  
  // Check category
  const validCategories = ['A', 'B', 'C', 'D', 'E', 'GENERAL']
  if (!validCategories.includes(test.category)) errors.push('Жарамсыз санат')
  
  return errors.join(', ') || 'Белгісіз қате'
}

const executeAutoClean = async () => {
  if (autoCleanConfirmation.value !== 'ТАЗАЛАУ') {
    snackbarStore.error('Растау мәтінін дұрыс жазыңыз')
    return
  }
  
  autoCleanLoading.value = true
  
  try {
    // Get all tests with errors
    const testsToClean = testsWithErrors.value.map(test => test.id)
    
    if (testsToClean.length === 0) {
      snackbarStore.warning('Тазаланатын тесттер жоқ')
      return
    }
    
    // Send auto-clean request to backend
    const response = await api.post('/admin/tests/auto-clean', {
      testIds: testsToClean,
      options: autoCleanOptions.value
    })
    
    const { cleaned, deleted, details } = response.data
    
    let successMessage = `Автоматты тазалау аяқталды:\n`
    if (cleaned > 0) successMessage += `- ${cleaned} тест тазаланды\n`
    if (deleted > 0) successMessage += `- ${deleted} тест өшірілді\n`
    if (details) successMessage += `- ${details}`
    
    snackbarStore.success(successMessage)
    
    // Reload tests and close dialog
    await loadTests()
    closeAutoCleanDialog()
    
    // Clear error filter to show all tests
    selectedErrorFilter.value = ''
    
  } catch (error) {
    // Error
    
    if (error.response?.status === 404) {
      snackbarStore.error('Auto-clean API табылмады')
    } else {
      const errorMessage = error.response?.data?.error || 'Автоматты тазалау кезінде қате кетті'
      snackbarStore.error(errorMessage)
    }
  } finally {
    autoCleanLoading.value = false
  }
}

const confirmDeleteAll = async () => {
  if (confirmationText.value !== 'ӨШІРУ') {
    snackbarStore.error('Растау мәтінін дұрыс жазыңыз')
    return
  }

  deletingAll.value = true
  try {
    // Get all test IDs to delete
    const testIds = filteredTests.value.map(test => test.id)
    
    if (testIds.length === 0) {
      snackbarStore.warning('Өшірілетін тесттер жоқ')
      return
    }

    // Send delete request for all tests
    await api.delete('/admin/tests/bulk', {
      data: { testIds }
    })

    snackbarStore.success(`${testIds.length} тест сәтті өшірілді`)
    await loadTests()
    closeDeleteAllDialog()
  } catch (error) {
    // Error
    
    if (error.response?.status === 404) {
      snackbarStore.error('Bulk delete API табылмады')
    } else {
      snackbarStore.error('Тесттерді өшіру кезінде қате кетті')
    }
  } finally {
    deletingAll.value = false
  }
}

const viewStats = async (test) => {
  statsLoading.value = true
  currentTestStats.value = test
  
  try {
    const response = await api.get(`/admin/tests/${test.id}/stats`)
    currentTestStats.value.statistics = response.data
    statsDialog.value = true
  } catch (error) {
    // Error
    snackbarStore.error('Ошибка загрузки статистики теста')
  } finally {
    statsLoading.value = false
  }
}

const clearFilters = () => {
  searchQuery.value = ''
  selectedTopic.value = ''
  selectedErrorFilter.value = ''
}

// PDF Generator functions
const openPdfGeneratorDialog = () => {
  pdfConfig.value = {
    topics: [],
    language: 'ru'
  }
  pdfGeneratorDialog.value = true
}

const closePdfGeneratorDialog = () => {
  pdfGeneratorDialog.value = false
  pdfConfig.value = {
    topics: [],
    language: 'ru'
  }
}

const toggleAllTopics = () => {
  if (pdfConfig.value.topics.length === uniqueTopics.value.length) {
    pdfConfig.value.topics = []
  } else {
    pdfConfig.value.topics = [...uniqueTopics.value]
  }
}

// Normalize video source path for consistent URLs
const normalizeVideoSrc = (src) => {
  if (!src) return null

  const trimmed = src.trim()
  if (!trimmed) return null

  if (/^(?:https?:)?\/\//i.test(trimmed) || trimmed.startsWith('data:')) {
    return trimmed
  }

  let normalized = trimmed

  const withSlashSegmentIndex = normalized.indexOf('/videos/')
  if (withSlashSegmentIndex !== -1) {
    normalized = normalized.slice(withSlashSegmentIndex)
  } else {
    const segmentIndex = normalized.indexOf('videos/')
    if (segmentIndex !== -1) {
      normalized = normalized.slice(segmentIndex)
    }
  }

  if (!normalized.startsWith('/')) {
    normalized = `/${normalized}`
  }

  if (!normalized.startsWith('/videos/')) {
    const sanitized = normalized.replace(/^\/+/, '').replace(/^videos\//, '')
    normalized = `/videos/${sanitized}`
  }

  return withBasePath(normalized)
}

const generatePDF = async () => {
  try {
    generatingPdf.value = true
    
    // Get tests for selected topics
    const selectedTests = tests.value.filter(test => 
      pdfConfig.value.topics.includes(test.topic)
    )
    
    if (selectedTests.length === 0) {
      snackbarStore.error(t('admin.pdf.noTestsFound') || 'Тесттер табылмады')
      return
    }
    
    // Generate PDF
    await generateTestsPDF(selectedTests, pdfConfig.value)
    
    snackbarStore.success(t('admin.pdf.success', { count: selectedTests.length }) || `PDF сәтті жасалды (${selectedTests.length} тест)`)
    closePdfGeneratorDialog()
    
  } catch (error) {
    snackbarStore.error((t('admin.pdf.error') || 'PDF жасау кезінде қате') + ': ' + error.message)
  } finally {
    generatingPdf.value = false
  }
}

// Generate video thumbnail from video URL - captures last second of video
// Бір суретті data URI ретінде жүктеу (PDF iframe-де сенімді көрсету үшін)
const loadImageDataUrl = async (url) => {
  try {
    const res = await fetch(url)
    if (!res.ok) return null
    const blob = await res.blob()
    return await new Promise((resolve) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result)
      reader.onerror = () => resolve(null)
      reader.readAsDataURL(blob)
    })
  } catch (e) {
    return null
  }
}

const getVideoThumbnail = async (videoUrl) => {
  return new Promise((resolve) => {
    const video = document.createElement('video')
    video.crossOrigin = 'anonymous'
    video.muted = true
    video.playsInline = true
    video.preload = 'auto'

    let done = false
    const finish = (result) => {
      if (done) return
      done = true
      try { video.removeAttribute('src'); video.load() } catch (e) { /* ignore */ }
      video.remove()
      resolve(result)
    }

    const capture = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = 320
        canvas.height = 240
        const ctx = canvas.getContext('2d')
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8)
        finish(dataUrl)
      } catch (e) {
        console.error('Error capturing video frame:', e)
        finish(null)
      }
    }

    video.onloadedmetadata = () => {
      // Соңғы кадрды түсіреміз (ПДД видеосының мазмұны әдетте соңында).
      // duration Infinity/NaN болса — бастапқы кадрға түсеміз (жылдам, файл басы жеткілікті).
      const d = video.duration
      video.currentTime = (Number.isFinite(d) && d > 0.5) ? Math.max(0, d - 0.3) : 0.5
    }

    video.onseeked = capture

    video.onerror = () => finish(null)

    // Таймаут — 12 секунд (үлкен видеоларға уақыт беру). Уақыт бітсе "СУРЕТ ЖОҚ".
    setTimeout(() => finish(null), 12000)

    video.src = videoUrl
    video.load()
  })
}

// Batch generate thumbnails for all tests - PARALLEL processing for speed
const generateThumbnails = async (tests) => {
  const thumbnails = {}
  const BATCH_SIZE = 5 // Параллель 5 видео жүктеу
  
  // Тек video1 бар тесттерді фильтрлеу
  const testsWithVideo = tests.filter(t => t.video1)
  
  for (let i = 0; i < testsWithVideo.length; i += BATCH_SIZE) {
    const batch = testsWithVideo.slice(i, i + BATCH_SIZE)
    
    // Параллель жүктеу
    const results = await Promise.all(
      batch.map(async (test) => {
        const videoUrl = normalizeVideoSrc(test.video1)
        try {
          const thumbnail = await getVideoThumbnail(videoUrl)
          return { id: test.id, thumbnail }
        } catch (e) {
          return { id: test.id, thumbnail: null }
        }
      })
    )
    
    // Нәтижелерді сақтау
    results.forEach(r => {
      thumbnails[r.id] = r.thumbnail
    })
  }
  
  return thumbnails
}

const generateTestsPDF = async (tests, config) => {
  const lang = config.language
  const langSuffix = lang.charAt(0).toUpperCase() + lang.slice(1)
  
  // Get unique topics from tests
  const selectedTopics = [...new Set(tests.map(t => t.topic))].filter(Boolean)
  const topicsText = selectedTopics.join(', ')
  
  // i18n labels based on language
  const labels = {
    ru: {
      title: 'Тест водительской школы "Korkem Auto"',
      topics: 'Темы',
      noImage: 'СУРЕТ ЖОҚ',
      question: 'Вопрос',
      answer: 'Ответ'
    },
    kz: {
      title: '"Korkem Auto" жүргізушілер мектебінің тесті',
      topics: 'Тақырыптар',
      noImage: 'СУРЕТ ЖОҚ',
      question: 'Сұрақ',
      answer: 'Жауап'
    },
    en: {
      title: 'Driving School Test "Korkem Auto"',
      topics: 'Topics',
      noImage: 'NO IMAGE',
      question: 'Question',
      answer: 'Answer'
    }
  }
  const l = labels[lang] || labels.ru
  
  // Generate thumbnails for all tests (parallel batch processing)
  snackbarStore.info(t('admin.pdf.generatingThumbnails', { count: tests.length }))
  const thumbnails = await generateThumbnails(tests)
  // "novideo" placeholder — видеосы/суреті жоқ сұрақтарға брендтік логотип
  const placeholderImg = await loadImageDataUrl((import.meta.env.BASE_URL || '/') + 'images/korkem-logo.jpg')
  snackbarStore.info(t('admin.pdf.creatingPdf'))
  
  // Build HTML content with 2-column grid layout (6 questions per page)
  let html = `<div style="font-family: Arial, sans-serif; font-size: 8pt; line-height: 1.2; color: #000; background: white;">`

  const letters = ['1', '2', '3', '4', '5', '6']
  const questionsPerPage = 6
  const totalPages = Math.ceil(tests.length / questionsPerPage)
  
  // Process tests in groups of 6 (one page each)
  for (let page = 0; page < totalPages; page++) {
    const startIdx = page * questionsPerPage
    const endIdx = Math.min(startIdx + questionsPerPage, tests.length)
    const pageTests = tests.slice(startIdx, endIdx)
    
    // Start a page block
    html += `<div class="page-block" style="height: 277mm; padding: 3mm; box-sizing: border-box;">`
    
    // Header only on first page
    if (page === 0) {
      html += `<div style="text-align: center; margin-bottom: 5px; border-bottom: 2px solid #333; padding-bottom: 3px;">
        <h1 style="font-size: 12pt; font-weight: bold; margin: 0;">${l.title}</h1>
        <div style="font-size: 9pt; margin-top: 3px; color: #333;">${l.topics}: ${topicsText}</div>
      </div>`
    }

    // Answer-key table for this page — question numbers (blue) + correct answers (green)
    const keyCells = pageTests.map((test, k) => {
      const ca = test.correctAnswer
      const answerNum = (typeof ca === 'number' && ca >= 0) ? (ca + 1) : '—'
      return { q: startIdx + k + 1, a: answerNum }
    })
    const labelCell = (text, color) => `<td style="border: 1px solid #444; padding: 3px 10px; text-align: right; background: #f0f0f0; color: ${color}; font-weight: bold;">${text}</td>`
    html += `<table style="border-collapse: collapse; margin: 0 0 8px 0; font-size: 9pt; font-weight: bold;">
      <tr>${labelCell(l.question, '#1565C0')}${keyCells.map(c => `<td style="border: 1px solid #444; padding: 3px 12px; text-align: center; background: #E3F2FD; color: #1565C0;">${c.q}</td>`).join('')}</tr>
      <tr>${labelCell(l.answer, '#2E7D32')}${keyCells.map(c => `<td style="border: 1px solid #444; padding: 3px 12px; text-align: center; background: #E8F5E9; color: #2E7D32;">${c.a}</td>`).join('')}</tr>
    </table>`

    // Process tests in pairs (2 per row)
    for (let i = 0; i < pageTests.length; i += 2) {
      html += `<div class="row-block" style="display: flex; gap: 8px; width: 100%; margin-bottom: 12px;">`
      
      // Process up to 2 tests per row
      for (let j = 0; j < 2 && (i + j) < pageTests.length; j++) {
        const test = pageTests[i + j]
        const globalIndex = startIdx + i + j + 1
        const questionKey = `question${langSuffix}`
        const answersKey = `answers${langSuffix}`
        
        // Parse answers
        let options = []
        try {
          const answersData = test[answersKey] || test.answersRu || test.options || '[]'
          const parsed = typeof answersData === 'string' ? JSON.parse(answersData) : answersData
          
          if (Array.isArray(parsed)) {
            options = parsed
          } else if (typeof parsed === 'object') {
            options = Object.values(parsed)
          }
        } catch (e) {
          options = []
        }
        
        const questionText = test[questionKey] || test.questionRu || ''
        const thumbnail = thumbnails[test.id]
        
        // Each question takes 50% width
        html += `<div class="question-block" style="width: 50%; background: white;">`

        const numberBadge = `<span style="position: absolute; top: 3px; left: 3px; background: #333; color: white; padding: 2px 6px; font-size: 10pt; font-weight: bold; border-radius: 3px;">${globalIndex}</span>`

        html += `<div style="position: relative; width: 100%; height: 160px; background: #f7f7f7; border-radius: 3px; overflow: hidden; margin-bottom: 4px; display: flex; align-items: center; justify-content: center;">`
        if (thumbnail) {
          html += `<img src="${thumbnail}" style="width: 100%; height: 100%; object-fit: cover;" alt="" />`
        } else if (placeholderImg) {
          // Видео/сурет жоқ — брендтік "novideo" placeholder (логотип)
          html += `<img src="${placeholderImg}" style="max-width: 62%; max-height: 62%; object-fit: contain; opacity: 0.9;" alt="" />`
        } else {
          html += `<span style="color: #999; font-size: 8pt;">${l.noImage}</span>`
        }
        html += numberBadge
        html += `</div>`
        html += `<div style="font-size: 8pt; font-weight: bold; margin-bottom: 3px; line-height: 1.2;">${questionText}</div>`
        html += `<div style="font-size: 8pt; line-height: 1.25;">`
        
        // Add answers
        options.forEach((option, idx) => {
          let answerText = ''
          if (option.text) {
            answerText = option.text
          } else if (option[`text${langSuffix}`]) {
            answerText = option[`text${langSuffix}`]
          } else if (lang === 'ru' && option['РУ']) {
            answerText = option['РУ']
          } else if (lang === 'kz' && option['КЗ']) {
            answerText = option['КЗ']
          } else if (lang === 'en' && (option['АНГ'] || option['EN'])) {
            answerText = option['АНГ'] || option['EN']
          } else if (typeof option === 'string') {
            answerText = option
          }
          
          html += `<div style="margin: 1px 0;">${letters[idx]}. ${answerText}</div>`
        })
        
        html += `</div></div>`
      }
      
      // Close row block
      html += `</div>`
    }
    
    // Close page block
    html += `</div>`
  }
  
  html += `</div>`

  // Generate PDF using html2pdf.js
  snackbarStore.info(lang === 'kz' ? 'PDF файлы жасалуда...' : lang === 'en' ? 'Generating PDF file...' : 'Создание PDF файла...')
  
  // Create an iframe for isolated rendering
  const iframe = document.createElement('iframe')
  iframe.style.cssText = 'position: fixed; top: -9999px; left: -9999px; width: 210mm; border: none;'
  document.body.appendChild(iframe)
  
  await new Promise(resolve => setTimeout(resolve, 100))
  
  const iframeDoc = iframe.contentDocument || iframe.contentWindow.document
  iframeDoc.open()
  iframeDoc.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { background: white; font-family: Arial, sans-serif; }
        .page-block {
          page-break-after: always !important;
          break-after: page !important;
        }
        .page-block:last-child {
          page-break-after: avoid !important;
        }
        .question-block {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
        .row-block {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
      </style>
    </head>
    <body>${html}</body>
    </html>
  `)
  iframeDoc.close()
  
  await new Promise(resolve => setTimeout(resolve, 500))
  
  const container = iframeDoc.body.firstElementChild
  
  // Wait for images to load
  const images = iframeDoc.querySelectorAll('img')
  if (images.length > 0) {
    await Promise.all(
      Array.from(images).map(img => {
        if (img.complete) return Promise.resolve()
        return new Promise(resolve => {
          img.onload = resolve
          img.onerror = resolve
        })
      })
    )
  }
  await new Promise(resolve => setTimeout(resolve, 300))
  
  // PDF options - row-block keeps 2 questions together on same page
  const opt = {
    margin: [8, 8, 8, 8],
    filename: `tests_${config.language}_${Date.now()}.pdf`,
    image: { type: 'jpeg', quality: 0.92 },
    html2canvas: { 
      scale: 1.5,
      useCORS: true,
      logging: false,
      allowTaint: true,
      backgroundColor: '#ffffff'
    },
    jsPDF: { 
      unit: 'mm', 
      format: 'a4', 
      orientation: 'portrait'
    },
    pagebreak: { 
      mode: 'css',
      after: '.page-block',
      avoid: ['.row-block', '.question-block']
    }
  }
  
  try {
    await html2pdf().set(opt).from(container).save()
    snackbarStore.success(lang === 'kz' ? 'PDF сәтті жасалды!' : lang === 'en' ? 'PDF generated successfully!' : 'PDF успешно создан!')
  } catch (err) {
    console.error('PDF generation error:', err)
    snackbarStore.error(lang === 'kz' ? 'PDF жасау қатесі: ' + err.message : lang === 'en' ? 'PDF generation error: ' + err.message : 'Ошибка создания PDF: ' + err.message)
  } finally {
    document.body.removeChild(iframe)
  }
}

const deleteVideo = async (field) => {
  if (!editingTest.value || !editingTest.value.id) return
  
  if (!confirm(t('admin.confirmDeleteVideo') || 'Видеоны өшіруді растайсыз ба?')) return

  try {
    deletingVideo.value = field
    await api.delete(`/admin/tests/${editingTest.value.id}/video/${field}`)
    
    // Update local state
    currentVideos.value[field] = null
    
    // Update test in list
    const testIndex = tests.value.findIndex(t => t.id === editingTest.value.id)
    if (testIndex !== -1) {
      tests.value[testIndex][field] = null
    }
    
    snackbarStore.success(t('admin.videoDeleted'))
  } catch (error) {
    console.error('Error deleting video:', error)
    snackbarStore.error(t('admin.videoDeleteError'))
  } finally {
    deletingVideo.value = null
  }
}

const previewVideo = (videoFilename) => {
  if (videoFilename && videoFilename !== 'default.mp4') {
    // Clean up filename
    let cleanFilename = videoFilename;
    if (cleanFilename.startsWith('/videos/')) {
      cleanFilename = cleanFilename.replace('/videos/', '');
    } else if (cleanFilename.startsWith('videos/')) {
      cleanFilename = cleanFilename.replace('videos/', '');
    }
    
    // Use BASE_URL to construct the correct path (handles /v2/ prefix in production)
    const baseUrl = import.meta.env.BASE_URL;
    const normalizedBase = baseUrl.endsWith('/') ? baseUrl : baseUrl + '/';
    const videoUrl = `${normalizedBase}videos/${cleanFilename}`;
    
    currentVideoUrl.value = videoUrl;
    videoPreviewDialog.value = true;
  } else {
    snackbarStore.info(t('admin.videoUnavailable'))
  }
}

const getCategoryColor = (category) => {
  const colors = {
    'A': 'green',
    'B': 'blue', 
    'C': 'orange',
    'D': 'red',
    'GENERAL': 'purple'
  }
  return colors[category] || 'grey'
}

const getDifficultyColor = (difficulty) => {
  const colors = {
    'EASY': 'green',
    'MEDIUM': 'orange',
    'HARD': 'red'
  }
  return colors[difficulty] || 'grey'
}

// ============================================
// DUPLICATE TESTS MANAGEMENT FUNCTIONS
// ============================================

const openDuplicatesDialog = async () => {
  duplicatesDialog.value = true
  selectedDuplicates.value = []
  await loadDuplicates()
}

const closeDuplicatesDialog = () => {
  duplicatesDialog.value = false
  duplicatesData.value = null
  selectedDuplicates.value = []
}

const loadDuplicates = async () => {
  duplicatesLoading.value = true
  try {
    const response = await api.get('/admin/tests/duplicates')
    duplicatesData.value = response.data
    selectedDuplicates.value = []
  } catch (error) {
    console.error('Error loading duplicates:', error)
    snackbarStore.error('Дубликаттарды жүктеу кезінде қате')
  } finally {
    duplicatesLoading.value = false
  }
}

const selectAllDuplicates = () => {
  if (!duplicatesData.value?.groups) return
  
  // Select all duplicates except the first (original) in each group
  const allDuplicateIds = []
  duplicatesData.value.groups.forEach(group => {
    group.tests.slice(1).forEach(test => {
      allDuplicateIds.push(test.id)
    })
  })
  selectedDuplicates.value = allDuplicateIds
}

const deleteSingleDuplicate = async (testId) => {
  try {
    deletingDuplicates.value = true
    await api.delete('/admin/tests/duplicates/bulk', {
      data: { testIds: [testId] }
    })
    snackbarStore.success('Дубликат өшірілді')
    await loadDuplicates()
    await loadTests()
  } catch (error) {
    console.error('Error deleting duplicate:', error)
    snackbarStore.error('Дубликатты өшіру кезінде қате')
  } finally {
    deletingDuplicates.value = false
  }
}

const deleteSelectedDuplicates = async () => {
  if (selectedDuplicates.value.length === 0) return
  
  try {
    deletingDuplicates.value = true
    await api.delete('/admin/tests/duplicates/bulk', {
      data: { testIds: selectedDuplicates.value }
    })
    snackbarStore.success(`${selectedDuplicates.value.length} дубликат өшірілді`)
    await loadDuplicates()
    await loadTests()
  } catch (error) {
    console.error('Error deleting duplicates:', error)
    snackbarStore.error('Дубликаттарды өшіру кезінде қате')
  } finally {
    deletingDuplicates.value = false
  }
}

const openAutoDeleteDuplicatesDialog = () => {
  duplicateDeleteConfirmation.value = ''
  keepStrategy.value = 'oldest'
  autoDeleteDuplicatesDialog.value = true
}

const executeAutoDeleteDuplicates = async () => {
  if (duplicateDeleteConfirmation.value !== 'ӨШІРУ') return
  
  try {
    deletingDuplicates.value = true
    const response = await api.post('/admin/tests/duplicates/delete', {
      keepStrategy: keepStrategy.value
    })
    
    snackbarStore.success(response.data.message)
    autoDeleteDuplicatesDialog.value = false
    await loadDuplicates()
    await loadTests()
  } catch (error) {
    console.error('Error auto-deleting duplicates:', error)
    snackbarStore.error('Дубликаттарды автоматты өшіру кезінде қате')
  } finally {
    deletingDuplicates.value = false
    duplicateDeleteConfirmation.value = ''
  }
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('kk-KZ', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })
}

// ============================================
// LANGUAGE MISMATCH FUNCTIONS
// ============================================

const openLanguageMismatchDialog = async () => {
  languageMismatchDialog.value = true
  await loadLanguageMismatches()
}

const closeLanguageMismatchDialog = () => {
  languageMismatchDialog.value = false
  languageMismatchData.value = null
}

const loadLanguageMismatches = async () => {
  languageMismatchLoading.value = true
  try {
    const response = await api.get('/admin/tests/language-mismatch')
    languageMismatchData.value = response.data
  } catch (error) {
    console.error('Error loading language mismatches:', error)
    snackbarStore.error('Тіл қателерін жүктеу кезінде қате')
  } finally {
    languageMismatchLoading.value = false
  }
}

// Lifecycle
onMounted(() => {
  loadTests()
  loadAiEnabled()
})
</script>

<style scoped>
.v-table {
  border-radius: 8px;
}

.v-card-title {
  background-color: rgba(var(--v-theme-primary), 0.1);
}

.bg-success-lighten-5 {
  background-color: rgba(var(--v-theme-success), 0.1) !important;
}

/* Compact form styles */
.compact-form-card :deep(.v-field__input) {
  font-size: 0.85rem !important;
  min-height: 32px !important;
  padding-top: 4px !important;
  padding-bottom: 4px !important;
}

.compact-form-card :deep(.v-field__label) {
  font-size: 0.85rem !important;
  top: 4px !important;
}

.compact-form-card :deep(.v-input__details) {
  padding-inline: 8px !important;
  min-height: 14px !important;
  padding-top: 2px !important;
}

.compact-input :deep(.v-field__input) {
  font-size: 0.85rem !important;
  min-height: 32px !important;
  padding-top: 4px !important;
  padding-bottom: 4px !important;
}

.compact-input :deep(.v-label) {
  font-size: 0.85rem !important;
}

.compact-checkbox :deep(.v-label) {
  font-size: 0.85rem !important;
}

.compact-checkbox :deep(.v-selection-control) {
  min-height: 32px !important;
}
</style>
